import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { 
  Users, Ban, Trash2, Shield, ShieldOff, ShieldCheck,
  RefreshCw, X, AlertTriangle, UserX, BarChart3, MessageSquare, Search,
  CheckCircle, XCircle, Clock, UserCheck, Star, Eye, Crown, UserCog, MapPin, FileText, Award, ClipboardList,
  Flag, MessageCircle, StickyNote, Settings, Lock, Radio, Film, Plus, Youtube, Play, ExternalLink, Trophy, Gavel
} from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";
import AdminRadioManager from "./AdminRadioManager";
import ModerationLogPanel from "./ModerationLogPanel";import ModerationActionModal from "./ModerationActionModal";
import RoomWidgetControls from "./RoomWidgetControls";
import AdminHouseholdsPanel from "./AdminHouseholdsPanel";
import VoiceBansManager from "./VoiceBansManager";

// User role badge component
const UserRoleBadge = ({ role, isAdmin, isModerator }) => {
  if (isAdmin) {
    // Admin: red box, white border, black "A"
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-600 border-2 border-white text-black mr-1">
        A
      </span>
    );
  }
  if (isModerator) {
    // Moderator: blue box, red border, white "M"
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-blue-600 border-2 border-red-500 text-white mr-1">
        M
      </span>
    );
  }
  if (role === "guest") {
    // Guest: yellow box, green border, green "G"
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-yellow-400 border-2 border-green-500 text-green-600 mr-1">
        G
      </span>
    );
  }
  // Standard/VIP/Temp: green box, black border, black "Y"
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-green-500 border-2 border-black text-black mr-1">
      Y
    </span>
  );
};

const AdminPanel = ({ onClose, onStartPM }) => {
  const { token, user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [deniedLog, setDeniedLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("online");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [approveType, setApproveType] = useState("standard");
  const [showUserMenu, setShowUserMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [bannedIPs, setBannedIPs] = useState([]);
  const [newBanIP, setNewBanIP] = useState("");
  const [newBanReason, setNewBanReason] = useState("");
  const [registrationLog, setRegistrationLog] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [userNotes, setUserNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [roomAccessRequests, setRoomAccessRequests] = useState([]);
  const [systemBroadcastMessage, setSystemBroadcastMessage] = useState("");
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [systemBotPresets, setSystemBotPresets] = useState([]);
  const [newPresetLabel, setNewPresetLabel] = useState("");
  const [newPresetContent, setNewPresetContent] = useState("");
  const [editingPreset, setEditingPreset] = useState(null);
  
  // Login tracking state
  const [loginHistory, setLoginHistory] = useState([]);
  const [dailyLoginStats, setDailyLoginStats] = useState(null);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [selectedUserForVIP, setSelectedUserForVIP] = useState(null);
  const [vipPermissionsForm, setVipPermissionsForm] = useState({
    bypass_room_capacity: true,
    no_kick_ban: true,
    vip_badge: true
  });

  // Vouch system state
  const [vouchedUsers, setVouchedUsers] = useState([]);
  const [vouchSearchQuery, setVouchSearchQuery] = useState("");
  const [vouchSearchResults, setVouchSearchResults] = useState([]);
  const [vouchingUser, setVouchingUser] = useState(false);

  // Contact submissions state
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [unreadContactCount, setUnreadContactCount] = useState(0);

  // Adult Employees state
  const [adultEmployees, setAdultEmployees] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [addingEmployee, setAddingEmployee] = useState(false);

  // Movie Playlists state
  const [moviePlaylists, setMoviePlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [youtubePlaylistUrl, setYoutubePlaylistUrl] = useState("");
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);

  // Movie Suggestions state (Wanted Movies Log)
  const [movieSuggestions, setMovieSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState("all");
  const [pendingSuggestionsCount, setPendingSuggestionsCount] = useState(0);

  // Monitored Rooms state
  const [monitoredMessages, setMonitoredMessages] = useState([]);
  const [monitoredUnreadCounts, setMonitoredUnreadCounts] = useState({});
  const [monitoredRoomFilter, setMonitoredRoomFilter] = useState("all");
  const [loadingMonitored, setLoadingMonitored] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  // Staff Management state
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDesc, setNewTaskDesc] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("normal");

  // Staff Timecard state
  const [timecardData, setTimecardData] = useState(null);
  const [selectedTimecardStaff, setSelectedTimecardStaff] = useState(null);
  const [timecardPeriod, setTimecardPeriod] = useState("week");
  const [loadingTimecard, setLoadingTimecard] = useState(false);

  // Admin Announcements state
  const [announcementSubject, setAnnouncementSubject] = useState("");
  const [announcementContent, setAnnouncementContent] = useState("");
  const [announcementPriority, setAnnouncementPriority] = useState("normal");
  const [announcementTargets, setAnnouncementTargets] = useState("all"); // all or selected
  const [selectedAnnouncementUsers, setSelectedAnnouncementUsers] = useState([]);
  const [announcementsList, setAnnouncementsList] = useState([]);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);

  // User Category Filter state
  const [userCategoryFilter, setUserCategoryFilter] = useState("all");

  // Moderation Action Modal state
  const [showModerationModal, setShowModerationModal] = useState(false);
  const [moderationTargetUser, setModerationTargetUser] = useState(null);

  // Voting System state
  const [votingPolls, setVotingPolls] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [newPollTitle, setNewPollTitle] = useState("");
  const [newPollDescription, setNewPollDescription] = useState("");
  const [newPollType, setNewPollType] = useState("text"); // text, single_choice, multiple_choice
  const [newPollOptions, setNewPollOptions] = useState(["", ""]); // Options for single/multiple choice
  const [newPollExpiry, setNewPollExpiry] = useState(""); // Expiry date
  const [loadingPolls, setLoadingPolls] = useState(false);

  // Age Verification state
  const [pendingAgeVerifications, setPendingAgeVerifications] = useState([]);
  const [loadingAgeVerifications, setLoadingAgeVerifications] = useState(false);

  // Ask Rooms state
  const [askRoomHosts, setAskRoomHosts] = useState({});
  const [askRoomHostInput, setAskRoomHostInput] = useState({});
  const [newNote, setNewNote] = useState("");

  // Prize pool / Petty cash state
  const [prizePool, setPrizePool] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  // Elite VIP state
  const [eliteVIPUsers, setEliteVIPUsers] = useState([]);
  const [eliteVIPSearchQuery, setEliteVIPSearchQuery] = useState("");
  const [eliteVIPSearchResults, setEliteVIPSearchResults] = useState([]);
  const [addingEliteVIP, setAddingEliteVIP] = useState(false);

  // Yana account settings
  const [yanaAccountUsername, setYanaAccountUsername] = useState("");
  const [settingYanaAccount, setSettingYanaAccount] = useState(false);

  // Super admin username - cannot be modified by other admins
  const SUPER_ADMIN = "mr_fuker";
  const isSuperAdmin = currentUser?.username === SUPER_ADMIN;

  // Default permissions structure
  const DEFAULT_PERMISSIONS = {
    executive_admin: {
      approve_users: true,
      deny_users: true,
      ban_users: true,
      ban_ips: true,
      unban_users: true,
      unban_ips: true,
      boot_users: true,
      delete_users: true,
      make_moderator: true,
      remove_moderator: true,
      make_admin: true,
      remove_admin: false,
      make_executive_admin: false,
      remove_executive_admin: false,
      view_ips: true,
      view_emails: true,
      view_notes: true,
      add_notes: true,
      view_reg_log: true,
      view_denied_log: true,
      review_applications: true,
      clear_messages: true,
      view_monitored_rooms: true,
      reply_monitored_rooms: true,
      manage_movie_playlists: true,
      manage_wanted_movies: true,
      manage_music: true,
      view_contact_messages: true,
      change_radio_stations: true
    },
    admin: {
      approve_users: true,
      deny_users: true,
      ban_users: true,
      ban_ips: true,
      unban_users: true,
      unban_ips: true,
      boot_users: true,
      delete_users: true,
      make_moderator: true,
      remove_moderator: true,
      make_admin: false,
      remove_admin: false,
      make_executive_admin: false,
      remove_executive_admin: false,
      view_ips: true,
      view_emails: true,
      view_notes: true,
      add_notes: true,
      view_reg_log: true,
      view_denied_log: true,
      review_applications: true,
      clear_messages: true,
      view_monitored_rooms: true,
      reply_monitored_rooms: true,
      manage_movie_playlists: true,
      manage_wanted_movies: true,
      manage_music: true,
      view_contact_messages: true,
      change_radio_stations: true
    },
    moderator: {
      approve_users: false,
      deny_users: false,
      ban_users: false,
      ban_ips: false,
      unban_users: false,
      unban_ips: false,
      boot_users: true,
      delete_users: false,
      make_moderator: false,
      remove_moderator: false,
      make_admin: false,
      remove_admin: false,
      make_executive_admin: false,
      remove_executive_admin: false,
      view_ips: false,
      view_emails: false,
      view_notes: true,
      add_notes: true,
      view_reg_log: false,
      view_denied_log: false,
      review_applications: false,
      clear_messages: true,
      view_monitored_rooms: false,
      reply_monitored_rooms: false,
      manage_movie_playlists: false,
      manage_wanted_movies: false,
      manage_music: false,
      view_contact_messages: false,
      change_radio_stations: false
    }
  };

  // Check if current user has a specific permission
  const hasPermission = (permissionKey) => {
    if (isSuperAdmin) return true; // Super admin has all permissions
    if (!permissions) return false;
    
    // Determine role hierarchy: executive_admin > admin > moderator
    let role = null;
    if (currentUser?.is_executive_admin) {
      role = 'executive_admin';
    } else if (currentUser?.is_admin) {
      role = 'admin';
    } else if (currentUser?.is_moderator) {
      role = 'moderator';
    }
    
    if (!role) return false;
    
    return permissions[role]?.[permissionKey] ?? false;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, pendingRes, onlineRes, deniedRes, bannedRes, regLogRes, appsRes, permsRes, roomReqsRes, broadcastRes, presetsRes, dailyStatsRes, loginHistoryRes, contactRes, unreadContactRes, employeesRes, suggestionsCountRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/online`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/denied-log`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/banned-ips`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/registration-log`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/permissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/room-access-requests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/broadcasts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/system-bot-presets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/daily-login-stats?days=7`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/login-history?days=30`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { login_history: [] } })),
        axios.get(`${API}/admin/contact-submissions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { submissions: [] } })),
        axios.get(`${API}/admin/contact-submissions/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/admin/adult-employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { employees: [] } })),
        axios.get(`${API}/admin/movie-suggestions?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { suggestions: [] } }))
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
      setOnlineUsers(onlineRes.data);
      setDeniedLog(deniedRes.data);
      setBannedIPs(bannedRes.data);
      setRegistrationLog(regLogRes.data);
      setApplications(appsRes.data);
      setPermissions(permsRes.data || DEFAULT_PERMISSIONS);
      setRoomAccessRequests(roomReqsRes.data);
      setBroadcastHistory(broadcastRes.data || []);
      setSystemBotPresets(presetsRes.data || []);
      setDailyLoginStats(dailyStatsRes.data);
      setLoginHistory(loginHistoryRes.data?.login_history || []);
      setContactSubmissions(contactRes.data?.submissions || []);
      setUnreadContactCount(unreadContactRes.data?.unread_count || 0);
      setAdultEmployees(employeesRes.data?.employees || []);
      setPendingSuggestionsCount(suggestionsCountRes.data?.suggestions?.length || 0);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Removed auto-refresh interval - use manual Refresh button instead
    // This prevents UI blinking while filling out forms
  }, [token]);

  // Auto-refresh login history every 15 seconds when viewing login log tab
  useEffect(() => {
    if (activeTab !== "login_log") return;
    
    const refreshLoginHistory = async () => {
      try {
        const res = await axios.get(`${API}/admin/login-history?days=30`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setLoginHistory(res.data?.login_history || []);
      } catch (error) {
        console.error("Failed to refresh login history:", error);
      }
    };
    
    const interval = setInterval(refreshLoginHistory, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, [activeTab, token]);

  // Fetch vouched users
  const fetchVouchedUsers = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await axios.get(`${API}/admin/vouched-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVouchedUsers(response.data.vouched_users || []);
    } catch (error) {
      console.error("Failed to fetch vouched users:", error);
    }
  };

  // Fetch petty cash (renamed from prize pool)
  const fetchPettyCash = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await axios.get(`${API}/admin/petty-cash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrizePool(response.data); // reusing state variable
    } catch (error) {
      console.error("Failed to fetch petty cash:", error);
    }
  };

  // Vouch a user
  const vouchUser = async (userId) => {
    setVouchingUser(true);
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/vouch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchVouchedUsers();
      setVouchSearchQuery("");
      setVouchSearchResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to vouch user");
    } finally {
      setVouchingUser(false);
    }
  };

  // Revoke vouch
  const revokeVouch = async (userId) => {
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}/vouch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchVouchedUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke vouch");
    }
  };

  // Search users for vouching
  const searchUsersForVouch = () => {
    if (!vouchSearchQuery.trim()) {
      setVouchSearchResults([]);
      return;
    }
    const query = vouchSearchQuery.toLowerCase();
    const results = users.filter(u => 
      u.username.toLowerCase().includes(query) && 
      !vouchedUsers.some(v => v.user_id === u.user_id)
    ).slice(0, 10);
    setVouchSearchResults(results);
  };

  // Withdraw from petty cash (renamed from prize pool)
  const [withdrawCurrency, setWithdrawCurrency] = useState("diamonds");
  const [depositCurrency, setDepositCurrency] = useState("diamonds");
  const [depositAmount, setDepositAmount] = useState("");
  const [depositReason, setDepositReason] = useState("");
  const [depositing, setDepositing] = useState(false);
  
  const withdrawFromPettyCash = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!withdrawReason.trim()) {
      toast.error("Enter a reason for withdrawal");
      return;
    }
    setWithdrawing(true);
    try {
      const response = await axios.post(`${API}/admin/petty-cash/withdraw`, 
        { currency: withdrawCurrency, amount, reason: withdrawReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setWithdrawAmount("");
      setWithdrawReason("");
      fetchPettyCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to withdraw");
    } finally {
      setWithdrawing(false);
    }
  };

  // Deposit to petty cash
  const depositToPettyCash = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setDepositing(true);
    try {
      const response = await axios.post(`${API}/admin/petty-cash/deposit`, 
        { currency: depositCurrency, amount, reason: depositReason || "Admin deposit" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setDepositAmount("");
      setDepositReason("");
      fetchPettyCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deposit");
    } finally {
      setDepositing(false);
    }
  };

  // Set Yana account (for Ask Yana room)
  const setYanaAccount = async () => {
    if (!yanaAccountUsername.trim()) {
      toast.error("Enter a username");
      return;
    }
    setSettingYanaAccount(true);
    try {
      const response = await axios.post(`${API}/admin/ask-yana/set-account`,
        { username: yanaAccountUsername.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setYanaAccountUsername("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set Yana account");
    } finally {
      setSettingYanaAccount(false);
    }
  };

  // Elite VIP functions
  const fetchEliteVIPUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/elite-vip/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEliteVIPUsers(response.data.elite_vip_users || []);
    } catch (error) {
      console.error("Failed to fetch Elite VIP list:", error);
    }
  };

  const addEliteVIP = async (username) => {
    setAddingEliteVIP(true);
    try {
      const response = await axios.post(`${API}/admin/elite-vip/add`,
        { username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setEliteVIPSearchQuery("");
      setEliteVIPSearchResults([]);
      fetchEliteVIPUsers();
      fetchData(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to Elite VIP");
    } finally {
      setAddingEliteVIP(false);
    }
  };

  const removeEliteVIP = async (username) => {
    try {
      const response = await axios.delete(`${API}/admin/elite-vip/remove/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchEliteVIPUsers();
      fetchData(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from Elite VIP");
    }
  };

  const searchUsersForEliteVIP = () => {
    if (!eliteVIPSearchQuery.trim()) {
      setEliteVIPSearchResults([]);
      return;
    }
    const query = eliteVIPSearchQuery.toLowerCase();
    const results = users.filter(u => 
      u.username.toLowerCase().includes(query) && 
      !eliteVIPUsers.some(v => v.user_id === u.user_id)
    ).slice(0, 10);
    setEliteVIPSearchResults(results);
  };

  // Load vouch, elite vip, petty cash, and movie playlists data when tabs are selected
  useEffect(() => {
    if (activeTab === 'vouch') {
      fetchVouchedUsers();
    } else if (activeTab === 'elite_vip') {
      fetchEliteVIPUsers();
    } else if (activeTab === 'petty_cash') {
      fetchPettyCash();
    } else if (activeTab === 'movie_playlists') {
      fetchMoviePlaylists();
    } else if (activeTab === 'movie_suggestions') {
      fetchMovieSuggestions();
    } else if (activeTab === 'monitored_rooms') {
      fetchMonitoredMessages();
    } else if (activeTab === 'staff_management') {
      fetchStaffList();
    } else if (activeTab === 'staff_timecards') {
      fetchTimecards();
    } else if (activeTab === 'admin_announcements') {
      fetchAnnouncementsList();
    } else if (activeTab === 'voting_system') {
      fetchVotingPolls();
    } else if (activeTab === 'ask_rooms') {
      fetchAskRoomHosts();
    } else if (activeTab === 'age_verifications') {
      fetchPendingAgeVerifications();
    }
  }, [activeTab]);

  // Age Verification Functions
  const fetchPendingAgeVerifications = async () => {
    setLoadingAgeVerifications(true);
    try {
      const response = await axios.get(`${API}/admin/age-verification/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingAgeVerifications(response.data.pending || []);
    } catch (error) {
      console.error("Failed to fetch pending age verifications:", error);
    } finally {
      setLoadingAgeVerifications(false);
    }
  };

  const approveAgeVerification = async (userId, username) => {
    try {
      await axios.post(`${API}/admin/age-verification/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Age verified: ${username}`);
      fetchPendingAgeVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const denyAgeVerification = async (userId, username) => {
    try {
      await axios.post(`${API}/admin/age-verification/${userId}/deny`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Age verification denied for ${username}`);
      fetchPendingAgeVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny");
    }
  };

  // Voting System Functions
  const fetchVotingPolls = async () => {
    setLoadingPolls(true);
    try {
      const response = await axios.get(`${API}/admin/voting/all-polls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVotingPolls(response.data.polls || []);
      const active = response.data.polls?.find(p => p.is_active);
      setActivePoll(active || null);
    } catch (error) {
      console.error("Failed to fetch polls:", error);
    } finally {
      setLoadingPolls(false);
    }
  };

  const createVotingPoll = async () => {
    if (!newPollTitle.trim()) {
      toast.error("Enter a poll title");
      return;
    }
    
    // For choice-based polls, validate options
    const validOptions = newPollOptions.filter(opt => opt.trim());
    if ((newPollType === "single_choice" || newPollType === "multiple_choice") && validOptions.length < 2) {
      toast.error("Add at least 2 options for choice-based polls");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/voting/create-poll`, {
        title: newPollTitle.trim(),
        description: newPollDescription.trim(),
        poll_type: newPollType,
        options: newPollType !== "text" ? validOptions : [],
        expires_at: newPollExpiry || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Poll created!");
      setNewPollTitle("");
      setNewPollDescription("");
      setNewPollType("text");
      setNewPollOptions(["", ""]);
      setNewPollExpiry("");
      fetchVotingPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create poll");
    }
  };

  const addPollOption = () => {
    if (newPollOptions.length < 10) {
      setNewPollOptions([...newPollOptions, ""]);
    }
  };

  const removePollOption = (index) => {
    if (newPollOptions.length > 2) {
      setNewPollOptions(newPollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index, value) => {
    const updated = [...newPollOptions];
    updated[index] = value;
    setNewPollOptions(updated);
  };

  const togglePollActive = async (pollId, isActive) => {
    try {
      await axios.post(`${API}/admin/voting/update-poll`, {
        poll_id: pollId,
        is_active: !isActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isActive ? "Poll deactivated" : "Poll activated");
      fetchVotingPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update poll");
    }
  };

  // Ask Rooms Functions
  const ASK_ROOM_IDS = [
    { id: "ask-wildest-fantasy", name: "Ask Me About My Wildest Fantasy" },
    { id: "ask-jade-room", name: "Ask Jade" },
    { id: "ask-your-master", name: "Ask Your Master" },
    { id: "mother-may-i-room", name: "Mother May I" },
    { id: "forgive-me-room", name: "Forgive Me For I Have Sinned" },
    { id: "ask-yana-room", name: "Ask Yana" }
  ];

  const fetchAskRoomHosts = async () => {
    const hosts = {};
    for (const room of ASK_ROOM_IDS) {
      try {
        const response = await axios.get(`${API}/ask-room/${room.id}/status`);
        hosts[room.id] = response.data.host_username || null;
      } catch (error) {
        hosts[room.id] = null;
      }
    }
    setAskRoomHosts(hosts);
  };

  const setAskRoomHost = async (roomId) => {
    const username = askRoomHostInput[roomId];
    if (!username?.trim()) {
      toast.error("Enter a username");
      return;
    }
    try {
      await axios.post(`${API}/admin/ask-room/${roomId}/set-host`, {
        username: username.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Host set for ${ASK_ROOM_IDS.find(r => r.id === roomId)?.name}`);
      setAskRoomHostInput(prev => ({ ...prev, [roomId]: "" }));
      fetchAskRoomHosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set host");
    }
  };

  useEffect(() => {
    searchUsersForVouch();
  }, [vouchSearchQuery, users, vouchedUsers]);

  useEffect(() => {
    searchUsersForEliteVIP();
  }, [eliteVIPSearchQuery, users, eliteVIPUsers]);

  // Mark contact submission as read
  const markContactRead = async (contactId) => {
    try {
      await axios.post(`${API}/admin/contact-submissions/${contactId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSubmissions(prev => prev.map(c => 
        c.contact_id === contactId ? { ...c, read: true } : c
      ));
      setUnreadContactCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  // Mark all contacts as read
  const markAllContactsRead = async () => {
    try {
      await axios.post(`${API}/admin/contact-submissions/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSubmissions(prev => prev.map(c => ({ ...c, read: true })));
      setUnreadContactCount(0);
      toast.success("All messages marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  // Adult Employee management
  const searchUsersForEmployee = () => {
    if (!employeeSearchQuery.trim()) {
      setEmployeeSearchResults([]);
      return;
    }
    const query = employeeSearchQuery.toLowerCase();
    const results = users.filter(u => 
      !adultEmployees.some(e => e.user_id === u.user_id) &&
      (u.username?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
    ).slice(0, 10);
    setEmployeeSearchResults(results);
  };

  useEffect(() => {
    searchUsersForEmployee();
  }, [employeeSearchQuery, users, adultEmployees]);

  const addAdultEmployee = async (userId, displayName) => {
    setAddingEmployee(true);
    try {
      await axios.post(`${API}/admin/adult-employees/add`, 
        { user_id: userId, display_name: displayName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Employee added successfully!");
      setEmployeeSearchQuery("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add employee");
    }
    setAddingEmployee(false);
  };

  const removeAdultEmployee = async (userId) => {
    if (!window.confirm("Remove this employee?")) return;
    try {
      await axios.post(`${API}/admin/adult-employees/remove`, 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Employee removed");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove employee");
    }
  };

  // Movie Playlist Management Functions
  const fetchMoviePlaylists = async () => {
    if (!isSuperAdmin) return;
    setLoadingPlaylists(true);
    try {
      const response = await axios.get(`${API}/admin/movie-playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const playlists = response.data.playlists || [];
      setMoviePlaylists(playlists);
      
      // Update selected playlist if one is selected (to refresh its data)
      if (selectedPlaylist) {
        const updatedSelected = playlists.find(p => p.playlist_id === selectedPlaylist.playlist_id);
        if (updatedSelected) {
          setSelectedPlaylist(updatedSelected);
        } else {
          // Playlist was deleted, clear selection
          setSelectedPlaylist(null);
        }
      }
    } catch (error) {
      console.error("Failed to fetch movie playlists:", error);
      toast.error("Failed to load movie playlists");
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const createMoviePlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error("Please enter a playlist name");
      return;
    }
    try {
      await axios.post(`${API}/admin/movie-playlists`, 
        { name: newPlaylistName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Playlist created successfully!");
      setNewPlaylistName("");
      fetchMoviePlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create playlist");
    }
  };

  const deleteMoviePlaylist = async (playlistId) => {
    if (!window.confirm("Delete this playlist and all its videos?")) return;
    try {
      await axios.delete(`${API}/admin/movie-playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Playlist deleted");
      fetchMoviePlaylists();
      if (selectedPlaylist?.playlist_id === playlistId) {
        setSelectedPlaylist(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete playlist");
    }
  };

  const addVideoToPlaylist = async (playlistId) => {
    if (!newVideoUrl.trim() || !newVideoTitle.trim()) {
      toast.error("Please enter both video URL and title");
      return;
    }
    
    // Extract YouTube video ID from URL
    let videoId = newVideoUrl.trim();
    const ytPatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^[a-zA-Z0-9_-]{11}$/
    ];
    
    for (const pattern of ytPatterns) {
      const match = newVideoUrl.match(pattern);
      if (match) {
        videoId = match[1] || match[0];
        break;
      }
    }
    
    try {
      await axios.post(`${API}/admin/movie-playlists/${playlistId}/videos`, 
        { 
          video_id: videoId,
          title: newVideoTitle.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Video added to playlist!");
      setNewVideoUrl("");
      setNewVideoTitle("");
      fetchMoviePlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add video");
    }
  };

  const removeVideoFromPlaylist = async (playlistId, videoId) => {
    if (!window.confirm("Remove this video from the playlist?")) return;
    try {
      await axios.delete(`${API}/admin/movie-playlists/${playlistId}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Video removed");
      fetchMoviePlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove video");
    }
  };

  const importYouTubePlaylist = async (playlistId) => {
    if (!youtubePlaylistUrl.trim()) {
      toast.error("Please enter a YouTube playlist URL");
      return;
    }
    try {
      await axios.post(`${API}/admin/movie-playlists/${playlistId}/import-youtube`, 
        { youtube_url: youtubePlaylistUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("YouTube playlist imported!");
      setYoutubePlaylistUrl("");
      fetchMoviePlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import YouTube playlist");
    }
  };

  const togglePlaylistFeatured = async (playlistId, featured) => {
    try {
      await axios.post(`${API}/admin/movie-playlists/${playlistId}/featured`, 
        { featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(featured ? "Playlist marked as featured" : "Playlist removed from featured");
      fetchMoviePlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update playlist");
    }
  };

  // Movie Suggestions (Wanted Movies Log) Functions
  const fetchMovieSuggestions = async () => {
    if (!isSuperAdmin) return;
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/admin/movie-suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const suggestions = response.data.suggestions || [];
      setMovieSuggestions(suggestions);
      setPendingSuggestionsCount(suggestions.filter(s => s.status === "pending").length);
    } catch (error) {
      console.error("Failed to fetch movie suggestions:", error);
      toast.error("Failed to load movie suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const updateSuggestionStatus = async (suggestionId, status, adminNotes = null) => {
    try {
      await axios.put(`${API}/admin/movie-suggestions/${suggestionId}`, 
        { status, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Suggestion marked as ${status}`);
      fetchMovieSuggestions();
    } catch (error) {
      toast.error("Failed to update suggestion");
    }
  };

  const deleteSuggestion = async (suggestionId) => {
    try {
      await axios.delete(`${API}/admin/movie-suggestions/${suggestionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Suggestion deleted");
      fetchMovieSuggestions();
    } catch (error) {
      toast.error("Failed to delete suggestion");
    }
  };

  // Monitored Rooms Functions
  const fetchMonitoredMessages = async () => {
    if (!isSuperAdmin) return;
    setLoadingMonitored(true);
    try {
      const response = await axios.get(`${API}/admin/monitored-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitoredMessages(response.data.messages || []);
      setMonitoredUnreadCounts(response.data.unread_counts || {});
    } catch (error) {
      console.error("Failed to fetch monitored messages:", error);
      toast.error("Failed to load monitored messages");
    } finally {
      setLoadingMonitored(false);
    }
  };

  const markMonitoredRead = async (logId) => {
    try {
      await axios.post(`${API}/admin/monitored-messages/${logId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitoredMessages(prev => prev.map(m => 
        m.log_id === logId ? { ...m, read: true } : m
      ));
      fetchMonitoredMessages(); // Refresh counts
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const replyToMonitoredMessage = async (logId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/admin/monitored-messages/${logId}/reply`, 
        { reply: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent via IM!");
      setReplyingTo(null);
      setReplyText("");
      fetchMonitoredMessages();
    } catch (error) {
      toast.error("Failed to send reply");
    }
  };

  // Staff Management Functions
  const fetchStaffList = async () => {
    if (!isSuperAdmin) return;
    setLoadingStaff(true);
    try {
      const response = await axios.get(`${API}/admin/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
      toast.error("Failed to load staff list");
    } finally {
      setLoadingStaff(false);
    }
  };

  const addStaffTask = async (staffId) => {
    if (!newTaskTitle.trim()) return;
    try {
      await axios.post(`${API}/admin/staff/${staffId}/tasks`, {
        title: newTaskTitle,
        description: newTaskDesc || null,
        priority: newTaskPriority
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Task added!");
      setNewTaskTitle("");
      setNewTaskDesc("");
      setNewTaskPriority("normal");
      fetchStaffList();
    } catch (error) {
      toast.error("Failed to add task");
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/admin/staff/tasks/${taskId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStaffList();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/admin/staff/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStaffList();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const addStaffNote = async (staffId) => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/admin/staff/${staffId}/notes`, {
        content: newNote
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Note added!");
      setNewNote("");
      fetchStaffList();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Staff Timecard Functions
  const fetchTimecards = async () => {
    setLoadingTimecard(true);
    try {
      const response = await axios.get(`${API}/admin/staff/timecards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimecardData(response.data);
    } catch (error) {
      console.error("Failed to fetch timecards:", error);
      toast.error("Failed to load timecard data");
    } finally {
      setLoadingTimecard(false);
    }
  };

  const fetchIndividualTimecard = async (staffId, period = "week") => {
    try {
      const response = await axios.get(`${API}/admin/staff/timecard/${staffId}?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTimecardStaff(response.data);
    } catch (error) {
      toast.error("Failed to load staff timecard");
    }
  };

  // Admin Announcements Functions
  const sendAnnouncement = async () => {
    if (!announcementSubject.trim() || !announcementContent.trim()) {
      toast.error("Subject and content are required");
      return;
    }
    setSendingAnnouncement(true);
    try {
      const payload = {
        subject: announcementSubject,
        content: announcementContent,
        priority: announcementPriority,
        target_user_ids: announcementTargets === "all" ? null : selectedAnnouncementUsers
      };
      await axios.post(`${API}/admin/announcements/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Announcement sent to ${announcementTargets === "all" ? "all users" : selectedAnnouncementUsers.length + " users"}!`);
      setAnnouncementSubject("");
      setAnnouncementContent("");
      setAnnouncementPriority("normal");
      setSelectedAnnouncementUsers([]);
      fetchAnnouncementsList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send announcement");
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const fetchAnnouncementsList = async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncementsList(response.data.announcements || []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/admin/announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement deleted");
      fetchAnnouncementsList();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/admin/staff/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStaffList();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  // Handle room access request
  const handleRoomAccessRequest = async (requestId, approve) => {
    try {
      await axios.post(`${API}/admin/room-access-requests/${requestId}`, 
        { approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? "Access granted" : "Access denied");
      fetchData();
    } catch (error) {
      toast.error("Failed to process request");
    }
  };

  const handleAction = async (userId, action, reason = null) => {
    try {
      await axios.post(`${API}/admin/action`, 
        { user_id: userId, action, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Action "${action}" completed successfully`);
      fetchData();
      setSelectedUser(null);
      setActionReason("");
      setShowUserMenu(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleBanIP = async () => {
    if (!newBanIP.trim()) {
      toast.error("Please enter an IP address");
      return;
    }
    try {
      await axios.post(`${API}/admin/ban-ip`, 
        { ip: newBanIP.trim(), reason: newBanReason || "Manually banned" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`IP ${newBanIP} has been banned`);
      setNewBanIP("");
      setNewBanReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ban IP");
    }
  };

  const handleUnbanIP = async (ip) => {
    try {
      await axios.delete(`${API}/admin/ban-ip/${encodeURIComponent(ip)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`IP ${ip} has been unbanned`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unban IP");
    }
  };

  const handleReviewApplication = async (applicationId, action, notes = "") => {
    try {
      await axios.post(`${API}/admin/applications/${applicationId}/review`, {
        action,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application ${action === 'approve' ? 'approved' : 'denied'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review application");
    }
  };

  // Send system broadcast to all rooms
  const sendSystemBroadcast = async () => {
    if (!systemBroadcastMessage.trim()) {
      toast.error("Please enter a message to broadcast");
      return;
    }
    
    setSendingBroadcast(true);
    try {
      await axios.post(`${API}/admin/broadcast`, 
        { content: systemBroadcastMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("System broadcast sent to all chat rooms!");
      setSystemBroadcastMessage("");
      fetchData(); // Refresh to get updated history
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Preset management functions (super admin only)
  const createPreset = async () => {
    if (!newPresetLabel.trim() || !newPresetContent.trim()) {
      toast.error("Both label and content are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/system-bot-presets`, {
        label: newPresetLabel.trim(),
        content: newPresetContent.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preset created!");
      setNewPresetLabel("");
      setNewPresetContent("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create preset");
    }
  };

  const updatePreset = async (presetId) => {
    if (!editingPreset?.label?.trim() || !editingPreset?.content?.trim()) {
      toast.error("Both label and content are required");
      return;
    }
    try {
      await axios.put(`${API}/admin/system-bot-presets/${presetId}`, {
        label: editingPreset.label.trim(),
        content: editingPreset.content.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preset updated!");
      setEditingPreset(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update preset");
    }
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await axios.delete(`${API}/admin/system-bot-presets/${presetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preset deleted!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete preset");
    }
  };

  // Check if current user can modify target user
  const canModifyUser = (targetUser) => {
    // Super admin can modify anyone
    if (currentUser?.username === SUPER_ADMIN) return true;
    // Other admins cannot modify super admin
    if (targetUser.username === SUPER_ADMIN) return false;
    // Other admins can modify non-super-admin users
    return true;
  };

  // Handle user flag change
  const handleFlagChange = async (userId, flag) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/flag`, { flag }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User flag updated to ${flag}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update flag");
    }
  };

  // Handle opening notes modal
  const handleOpenNotes = async (targetUser) => {
    setShowNotesModal(targetUser);
    setNoteText("");
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API}/admin/users/${targetUser.user_id}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserNotes(response.data);
    } catch (error) {
      console.error("Failed to load notes:", error);
      setUserNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Handle adding a note
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/admin/users/${showNotesModal.user_id}/notes`, {
        note: noteText.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Note added");
      setNoteText("");
      // Reload notes
      const response = await axios.get(`${API}/admin/users/${showNotesModal.user_id}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserNotes(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add note");
    }
  };

  // Handle saving permissions (super admin only)
  const handleSavePermissions = async () => {
    if (!isSuperAdmin) return;
    try {
      await axios.post(`${API}/admin/permissions`, editingPermissions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Permissions saved!");
      setPermissions(editingPermissions);
      setShowPermissionsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save permissions");
    }
  };

  // Open permissions modal
  const openPermissionsModal = () => {
    // Deep merge backend permissions with defaults to ensure all roles exist
    const backendPerms = permissions || {};
    const mergedPerms = {
      executive_admin: { ...DEFAULT_PERMISSIONS.executive_admin, ...(backendPerms.executive_admin || {}) },
      admin: { ...DEFAULT_PERMISSIONS.admin, ...(backendPerms.admin || {}) },
      moderator: { ...DEFAULT_PERMISSIONS.moderator, ...(backendPerms.moderator || {}) }
    };
    setEditingPermissions(mergedPerms);
    setShowPermissionsModal(true);
  };

  // Toggle a permission
  const togglePermission = (role, key) => {
    setEditingPermissions(prev => {
      const rolePerms = prev[role] || {};
      return {
        ...prev,
        [role]: {
          ...rolePerms,
          [key]: !rolePerms[key]
        }
      };
    });
  };

  // Permission labels for display
  const PERMISSION_LABELS = {
    approve_users: "Approve Users",
    deny_users: "Deny Users",
    ban_users: "Ban Users",
    ban_ips: "Ban IP Addresses",
    unban_users: "Unban Users",
    unban_ips: "Unban IP Addresses",
    boot_users: "Boot/Kick Users",
    delete_users: "Delete Users",
    make_moderator: "Make Moderator",
    remove_moderator: "Remove Moderator",
    make_admin: "Make Admin",
    remove_admin: "Remove Admin",
    make_executive_admin: "Make Executive Admin",
    remove_executive_admin: "Remove Executive Admin",
    view_ips: "View IP Addresses",
    view_emails: "View Emails",
    view_notes: "View User Notes",
    add_notes: "Add User Notes",
    view_reg_log: "View Registration Log",
    view_denied_log: "View Denied Log",
    review_applications: "Review Applications",
    clear_messages: "Clear Room Messages",
    // New permissions for standard admins
    view_monitored_rooms: "View Monitored Rooms",
    reply_monitored_rooms: "Reply to Monitored Rooms",
    manage_movie_playlists: "Manage Movie Playlists",
    manage_wanted_movies: "Manage Wanted Movies",
    manage_music: "Manage Music Library",
    view_contact_messages: "View Contact Messages",
    change_radio_stations: "Change Radio Stations"
  };

  const handleUserClick = (e, targetUser) => {
    e.stopPropagation();
    // Center the menu in the screen instead of at click position
    setShowUserMenu(showUserMenu === targetUser.user_id ? null : targetUser.user_id);
  };

  // Filter users by search query and category
  const filteredUsers = users.filter(u => {
    // First, apply search filter
    const matchesSearch = u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Then, apply category filter
    switch (userCategoryFilter) {
      case 'admin':
        return u.is_admin && !u.is_executive_admin && u.username !== SUPER_ADMIN;
      case 'executive_admin':
        return u.is_executive_admin || u.username === SUPER_ADMIN;
      case 'moderator':
        return u.is_moderator && !u.is_admin;
      case 'vip':
        return u.user_role === 'vip' && !u.is_moderator && !u.is_admin;
      case 'elite_vip':
        return u.user_role === 'elite_vip' && !u.is_moderator && !u.is_admin;
      case 'all':
      default:
        return true;
    }
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString() + ' ' + new Date(dateStr).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  // Get flag color
  const getFlagColor = (flag) => {
    switch(flag) {
      case 'red': return 'bg-red-500';
      case 'yellow': return 'bg-yellow-400';
      default: return 'bg-green-500';
    }
  };

  // Get role display text
  const getRoleDisplay = (u) => {
    if (u.username === SUPER_ADMIN) return { text: 'Super Admin', color: 'bg-purple-700 text-white' };
    if (u.is_executive_admin) return { text: 'Exec Admin', color: 'bg-purple-600 text-white' };
    if (u.is_admin) return { text: 'Admin', color: 'bg-red-600 text-white' };
    if (u.is_moderator) return { text: 'Moderator', color: 'bg-blue-600 text-white' };
    if (u.user_role === 'guest') return { text: 'Guest', color: 'bg-gray-400 text-black' };
    if (u.user_role === 'elite_vip') return { text: 'Elite VIP', color: 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black' };
    if (u.user_role === 'vip') return { text: 'VIP', color: 'bg-yellow-400 text-black' };
    if (u.user_role === 'temporary') return { text: 'Temp', color: 'bg-orange-400 text-white' };
    return { text: 'Standard', color: 'bg-green-500 text-white' };
  };

  // User action dropdown menu
  const UserActionDropdown = ({ user: targetUser, position, onClose, isInModal }) => {
    const isSuperAdmin = targetUser.username === SUPER_ADMIN;
    const currentIsSuperAdmin = currentUser?.username === SUPER_ADMIN;
    
    // If target is super admin and current user is not super admin, show nothing
    if (isSuperAdmin && !currentIsSuperAdmin) {
      return (
        <div className="p-4 text-center">
          <Crown className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
          <p className="font-arial text-sm text-[#808080]">This user is protected</p>
        </div>
      );
    }
    
    const menuContent = (
      <div className={isInModal ? "" : "p-1 bg-[#C0C0C0]"}>
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
          onClick={() => { onStartPM?.(targetUser); setShowUserMenu(null); }}
        >
          <MessageSquare className="w-3 h-3" /> IM/PM Message
        </button>
        <div className="border-t border-[#808080] my-1" />
        {hasPermission("boot_users") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
          onClick={() => { handleAction(targetUser.user_id, "boot", "Booted by admin"); setShowUserMenu(null); }}
          data-testid="menu-boot-btn"
        >
          <UserX className="w-3 h-3 text-orange-500" /> Boot/Kick
        </button>
        )}
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-purple-50"
          onClick={() => { setModerationTargetUser(targetUser); setShowModerationModal(true); setShowUserMenu(null); }}
        >
          <Gavel className="w-3 h-3 text-purple-600" /> 📋 Take Moderation Action
        </button>
        {hasPermission("ban_users") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
          onClick={() => { setSelectedUser({ ...targetUser, action: 'ban' }); setShowUserMenu(null); }}
          data-testid="menu-ban-btn"
        >
          <Ban className="w-3 h-3 text-red-500" /> {targetUser.is_banned ? 'Already Banned' : 'Ban User'}
        </button>
        )}
        {hasPermission("unban_users") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-green-50"
          onClick={() => { handleAction(targetUser.user_id, "unban"); setShowUserMenu(null); }}
          data-testid="menu-unban-btn"
        >
          <ShieldCheck className="w-3 h-3 text-green-600" /> Unban User
        </button>
        )}
        {hasPermission("ban_ips") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-red-50"
          onClick={() => { setSelectedUser({ ...targetUser, action: 'ban_ip' }); setShowUserMenu(null); }}
          data-testid="menu-ban-ip-btn"
        >
          <AlertTriangle className="w-3 h-3 text-red-700" /> Ban IP Address
        </button>
        )}
        {hasPermission("unban_ips") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-green-50"
          onClick={() => { setSelectedUser({ ...targetUser, action: 'unban_ip' }); setShowUserMenu(null); }}
          data-testid="menu-unban-ip-btn"
        >
          <ShieldCheck className="w-3 h-3 text-green-600" /> Unban IP
        </button>
        )}
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
          onClick={() => { handleAction(targetUser.user_id, "block"); setShowUserMenu(null); }}
        >
          <XCircle className="w-3 h-3 text-red-600" /> Block
        </button>
        <div className="border-t border-[#808080] my-1" />
        {hasPermission("delete_users") && (
        <button 
          className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
          onClick={() => { setSelectedUser({ ...targetUser, action: 'delete' }); setShowUserMenu(null); }}
          data-testid="menu-delete-btn"
        >
          <Trash2 className="w-3 h-3 text-red-800" /> Delete User
        </button>
        )}
        <div className="border-t border-[#808080] my-1" />
        {/* VIP Management */}
        {targetUser.user_role !== 'vip' && !targetUser.is_admin && !targetUser.is_moderator && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-yellow-50"
            onClick={() => { handleAction(targetUser.user_id, "make_vip"); setShowUserMenu(null); }}
          >
            <Star className="w-3 h-3 text-yellow-500" /> Make VIP
          </button>
        )}
        {targetUser.user_role === 'vip' && (
          <>
            <button 
              className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 bg-yellow-50"
              onClick={() => { 
                setSelectedUserForVIP(targetUser);
                setVipPermissionsForm(targetUser.vip_permissions || {
                  bypass_room_capacity: true,
                  no_kick_ban: true,
                  vip_badge: true
                });
                setShowUserMenu(null);
              }}
            >
              <Settings className="w-3 h-3 text-yellow-600" /> VIP Permissions
            </button>
            <button 
              className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
              onClick={() => { handleAction(targetUser.user_id, "remove_vip"); setShowUserMenu(null); }}
            >
              <Star className="w-3 h-3 text-gray-400" /> Remove VIP
            </button>
          </>
        )}
        <div className="border-t border-[#808080] my-1" />
        {/* Admin Actions — gated by per-role permissions */}
        {!targetUser.is_admin && hasPermission("make_admin") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "make_admin"); setShowUserMenu(null); }}
            data-testid="menu-make-admin-btn"
          >
            <Crown className="w-3 h-3 text-yellow-500" /> Make Admin
          </button>
        )}
        {targetUser.is_admin && !targetUser.is_executive_admin && !isSuperAdmin && hasPermission("make_executive_admin") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "make_executive_admin"); setShowUserMenu(null); }}
            data-testid="menu-make-exec-admin-btn"
          >
            <Crown className="w-3 h-3 text-purple-500" /> Make Executive Admin
          </button>
        )}
        {targetUser.is_executive_admin && !isSuperAdmin && hasPermission("remove_executive_admin") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "remove_executive_admin"); setShowUserMenu(null); }}
            data-testid="menu-remove-exec-admin-btn"
          >
            <ShieldOff className="w-3 h-3 text-purple-300" /> Remove Executive Admin
          </button>
        )}
        {targetUser.is_admin && !isSuperAdmin && hasPermission("remove_admin") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "remove_admin"); setShowUserMenu(null); }}
            data-testid="menu-remove-admin-btn"
          >
            <ShieldOff className="w-3 h-3 text-gray-500" /> Remove Admin
          </button>
        )}
        {!targetUser.is_moderator && !targetUser.is_admin && hasPermission("make_moderator") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "make_moderator"); setShowUserMenu(null); }}
            data-testid="menu-make-mod-btn"
          >
            <Shield className="w-3 h-3 text-blue-500" /> Make Moderator
          </button>
        )}
        {targetUser.is_moderator && hasPermission("remove_moderator") && (
          <button 
            className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
            onClick={() => { handleAction(targetUser.user_id, "remove_moderator"); setShowUserMenu(null); }}
            data-testid="menu-remove-mod-btn"
          >
            <ShieldOff className="w-3 h-3 text-gray-500" /> Remove Moderator
          </button>
        )}
      </div>
    );
    
    // If in modal, just return the content
    if (isInModal) {
      return menuContent;
    }
    
    // Otherwise wrap in fixed position container
    return (
      <div 
        className="fixed bg-white retro-window z-50 min-w-[180px] shadow-lg"
        style={{ left: position.x, top: position.y }}
      >
        <div className="retro-title-bar py-0.5">
          <span className="text-xs">{targetUser.username}</span>
          <button className="text-xs px-1" onClick={() => setShowUserMenu(null)}>×</button>
        </div>
        {menuContent}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50" onClick={(e) => { if (e.target === e.currentTarget) { setShowUserMenu(null); } }}>
      <DraggableResizableWindow
        title={<><img src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png" alt="MEGA Chat" className="w-5 h-5 inline mr-2" />Admin Control Panel</>}
        onClose={onClose}
        initialPosition={{ x: Math.max(50, (window.innerWidth - 1000) / 2), y: 50 }}
        initialSize={{ width: 1000, height: 600 }}
        minSize={{ width: 600, height: 400 }}
        maxSize={{ width: 1400, height: 900 }}
        zIndex={9995}
        titleBarColor="bg-gradient-to-r from-[#7B0099] to-[#4B0082]"
      >
        <div className="p-2 bg-[#C0C0C0] h-full overflow-hidden flex flex-col" data-testid="admin-panel" onClick={() => setShowUserMenu(null)}>
          {/* Compact Tab Menu with Dropdowns */}
          <div className="flex gap-1 mb-2 items-center flex-wrap">
            {/* Users Dropdown */}
            <div className="relative">
              <select
                className="retro-button text-xs py-1 px-2 font-bold bg-gradient-to-r from-green-300 to-emerald-400 border-green-500 cursor-pointer"
                value={['online', 'pending', 'users', 'applications'].includes(activeTab) ? activeTab : ''}
                onChange={(e) => e.target.value && setActiveTab(e.target.value)}
                data-testid="users-dropdown"
              >
                <option value="" disabled>👥 Users</option>
                <option value="online">🟢 Online ({onlineUsers.length})</option>
                <option value="pending">⏳ Pending ({pendingUsers.length})</option>
                <option value="users">👥 All Users ({users.length})</option>
                <option value="applications">📋 Applications ({applications.length})</option>
              </select>
            </div>

            {/* Users & Employees Dropdown (Super Admin only) */}
            {isSuperAdmin && (
              <div className="relative">
                <select
                  className="retro-button text-xs py-1 px-2 font-bold bg-gradient-to-r from-pink-300 to-rose-400 border-pink-500 cursor-pointer"
                  value={['vouch', 'adult_employees', 'staff_management', 'staff_timecards', 'age_verifications', 'elite_vip', 'contact_messages', 'room_access'].includes(activeTab) ? activeTab : ''}
                  onChange={(e) => e.target.value && setActiveTab(e.target.value)}
                  data-testid="users-employees-dropdown"
                >
                  <option value="" disabled>👔 Users &amp; Employees</option>
                  <option value="vouch">✅ Vouch Users</option>
                  <option value="adult_employees">💋 Adult Employees ({adultEmployees.length})</option>
                  <option value="staff_management">👥 Staff Management</option>
                  <option value="staff_timecards">⏱️ Staff Timecards</option>
                  <option value="age_verifications">🔞 Age Verification{pendingAgeVerifications.length > 0 ? ` (${pendingAgeVerifications.length} NEW)` : ''}</option>
                  <option value="elite_vip">👑 Elite VIP / Delete VIP</option>
                  <option value="contact_messages">📧 Contact Messages {unreadContactCount > 0 ? `(${unreadContactCount} NEW)` : ''}</option>
                  <option value="room_access">🔐 Room Requests ({roomAccessRequests.length})</option>
                </select>
              </div>
            )}

            {/* Logs Dropdown */}
            <div className="relative">
              <select
                className="retro-button text-xs py-1 px-2 font-bold bg-gradient-to-r from-blue-300 to-indigo-400 border-blue-500 cursor-pointer"
                value={['banned', 'voice_bans', 'denied', 'reglog', 'login_log'].includes(activeTab) ? activeTab : ''}
                onChange={(e) => e.target.value && setActiveTab(e.target.value)}
                data-testid="logs-dropdown"
              >
                <option value="" disabled>📜 Logs</option>
                <option value="banned">🚫 Banned IPs ({bannedIPs.length})</option>
                <option value="voice_bans">🎙️ Voice Bans</option>
                <option value="denied">❌ Denied Log</option>
                <option value="reglog">📝 Reg Log</option>
                <option value="login_log">🕐 Login Log</option>
              </select>
            </div>

            {/* Tools Dropdown */}
            <div className="relative">
              <select
                className="retro-button text-xs py-1 px-2 font-bold bg-gradient-to-r from-purple-400 to-violet-500 border-purple-600 text-white cursor-pointer"
                value={['system_bot', 'radio_manager', 'movie_playlists', 'movie_suggestions', 'voting_system'].includes(activeTab) ? activeTab : ''}
                onChange={(e) => e.target.value && setActiveTab(e.target.value)}
                data-testid="tools-dropdown"
              >
                <option value="" disabled>🛠️ Tools</option>
                <option value="system_bot">🤖 System Bot</option>
                <option value="radio_manager">📻 Radio Stations</option>
                {isSuperAdmin && (
                  <>
                    <option value="movie_playlists">🎬 Movie Playlists</option>
                    <option value="movie_suggestions">📝 Wanted Movies {pendingSuggestionsCount > 0 ? `(${pendingSuggestionsCount} NEW)` : ''}</option>
                    <option value="voting_system">🏆 Voting System</option>
                  </>
                )}
              </select>
            </div>

            {/* Super Admin Dropdown (slimmed — moved most items into Users & Employees / Tools) */}
            {isSuperAdmin && (
              <div className="relative">
                <select
                  className="retro-button text-xs py-1 px-2 font-bold bg-gradient-to-r from-amber-400 to-yellow-500 border-amber-600 cursor-pointer"
                  value={['stats', 'petty_cash', 'monitored_rooms', 'admin_announcements', 'moderation_log', 'ask_rooms', 'households', 'room_widgets'].includes(activeTab) ? activeTab : ''}
                  onChange={(e) => e.target.value && setActiveTab(e.target.value)}
                  data-testid="superadmin-dropdown"
                >
                  <option value="" disabled>👑 Super Admin</option>
                  <option value="stats">📊 Stats</option>
                  <option value="petty_cash">💰 Petty Cash Box</option>
                  <option value="monitored_rooms">📡 Monitored Rooms {Object.values(monitoredUnreadCounts).reduce((a,b) => a+b, 0) > 0 ? `(${Object.values(monitoredUnreadCounts).reduce((a,b) => a+b, 0)} NEW)` : ''}</option>
                  <option value="admin_announcements">📢 Admin Announcements</option>
                  <option value="moderation_log">⚖️ Moderation Log</option>
                  <option value="ask_rooms">💬 Ask Room Hosts</option>
                  <option value="households">🏠 Households / Family Mode</option>
                  <option value="room_widgets">🧩 Room Widgets / Embeds</option>
                </select>
              </div>
            )}

            {/* Contact Messages Button with Notification Badge */}
            {isSuperAdmin && unreadContactCount > 0 && (
              <button 
                className="retro-button text-xs py-1 px-2 flex items-center gap-1 bg-red-500 text-white border-red-600 animate-pulse"
                onClick={() => setActiveTab('contact_messages')}
                data-testid="contact-notification-btn"
              >
                📧 {unreadContactCount} NEW
              </button>
            )}

            {/* Movie Suggestions Notification Badge */}
            {isSuperAdmin && pendingSuggestionsCount > 0 && (
              <button 
                className="retro-button text-xs py-1 px-2 flex items-center gap-1 bg-purple-500 text-white border-purple-600 animate-pulse"
                onClick={() => setActiveTab('movie_suggestions')}
                data-testid="suggestions-notification-btn"
              >
                🎬 {pendingSuggestionsCount} NEW
              </button>
            )}

            {/* Permissions button - only for super admin */}
            {isSuperAdmin && (
              <button 
                className="retro-button text-xs py-1 px-2 flex items-center gap-1 bg-[#7B0099] text-white border-[#7B0099]"
                onClick={openPermissionsModal}
                data-testid="permissions-btn"
              >
                <Settings className="w-3 h-3" /> Perms
              </button>
            )}

            <div className="flex-1" />
            
            {/* Current Tab Indicator */}
            <span className="text-xs font-bold text-[#7B0099] bg-white px-2 py-0.5 border border-[#7B0099]">
              {activeTab === 'online' && `🟢 Online (${onlineUsers.length})`}
              {activeTab === 'pending' && `⏳ Pending (${pendingUsers.length})`}
              {activeTab === 'users' && `👥 All Users (${users.length})`}
              {activeTab === 'applications' && `📋 Apps (${applications.length})`}
              {activeTab === 'banned' && `🚫 Banned (${bannedIPs.length})`}
              {activeTab === 'voice_bans' && '🎙️ Voice Bans'}
              {activeTab === 'denied' && '❌ Denied'}
              {activeTab === 'reglog' && '📝 Reg Log'}
              {activeTab === 'login_log' && '🕐 Login Log'}
              {activeTab === 'stats' && '📊 Stats'}
              {activeTab === 'system_bot' && '🤖 System Bot'}
              {activeTab === 'radio_manager' && '📻 Radio'}
              {activeTab === 'room_access' && `🔐 Room Req (${roomAccessRequests.length})`}
              {activeTab === 'vouch' && '✅ Vouch'}
              {activeTab === 'elite_vip' && '👑 Elite VIP'}
              {activeTab === 'petty_cash' && '💰 Petty Cash'}
              {activeTab === 'contact_messages' && `📧 Messages (${unreadContactCount} unread)`}
              {activeTab === 'adult_employees' && `💋 Employees (${adultEmployees.length})`}
              {activeTab === 'movie_playlists' && '🎬 Movie Playlists'}
              {activeTab === 'movie_suggestions' && '📝 Wanted Movies Log'}
              {activeTab === 'monitored_rooms' && '📡 Monitored Rooms'}
              {activeTab === 'staff_management' && '👥 Staff Management'}
              {activeTab === 'staff_timecards' && '⏱️ Staff Timecards'}
              {activeTab === 'admin_announcements' && '📢 Admin Announcements'}
              {activeTab === 'moderation_log' && '⚖️ Moderation Log'}
              {activeTab === 'age_verifications' && `🔞 Age Verification (${pendingAgeVerifications.length})`}
              {activeTab === 'households' && '🏠 Households'}
            </span>

            <button 
              className="retro-button text-xs py-1 px-2 flex items-center gap-1"
              onClick={fetchData}
              data-testid="refresh-admin-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loading && activeTab !== 'online' ? (
            <div className="retro-inset flex-1 flex items-center justify-center">
              <p className="font-arial text-sm">Loading...</p>
            </div>
          ) : (
            <>
              {/* Online Users Tab */}
              {activeTab === "online" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Online Users ({onlineUsers.length})
                  </h3>
                  
                  {onlineUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No users online</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {onlineUsers.map((u) => (
                        <div 
                          key={u.user_id} 
                          className="retro-window p-2 flex items-center justify-between hover:bg-[#E8E8E8]"
                          data-testid={`online-user-${u.user_id}`}
                        >
                          <div className="flex items-center gap-2 flex-1 flex-wrap">
                            <UserRoleBadge role={u.user_role} isAdmin={u.is_admin} isModerator={u.is_moderator} />
                            <button
                              className="font-verdana text-sm font-bold text-[#000080] hover:underline cursor-pointer"
                              onClick={(e) => handleUserClick(e, u)}
                            >
                              {u.username}
                            </button>
                            {/* Presence badge: In Room / In Lobby / Invisible */}
                            {u.presence_status === 'Invisible' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-white rounded font-bold" data-testid={`presence-badge-${u.user_id}`}>
                                👻 Invisible
                              </span>
                            )}
                            {u.presence_status === 'In Lobby' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-bold" data-testid={`presence-badge-${u.user_id}`}>
                                🏠 In Lobby
                              </span>
                            )}
                            {u.presence_status === 'In Room' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 rounded font-bold" data-testid={`presence-badge-${u.user_id}`}>
                                💬 In Room
                              </span>
                            )}
                            <span className="text-xs text-[#808080]">|</span>
                            <span className="text-xs text-[#666]">{u.email}</span>
                            <span className="text-xs text-[#808080]">|</span>
                            <span className="text-xs text-[#999] font-mono">{u.last_ip || 'N/A'}</span>
                            {u.current_room && u.presence_status === 'In Room' && (
                              <>
                                <span className="text-xs text-[#808080]">|</span>
                                <span className="text-xs text-green-600">Room: {u.current_room}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Centered User Action Menu for Online Users */}
                  {showUserMenu && onlineUsers.find(u => u.user_id === showUserMenu) && canModifyUser(onlineUsers.find(u => u.user_id === showUserMenu) || {}) && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowUserMenu(null)}>
                      <div className="retro-window w-64" onClick={e => e.stopPropagation()}>
                        <div className="retro-title-bar">
                          <span>User Actions</span>
                          <button className="close-button-visible" onClick={() => setShowUserMenu(null)}>✕</button>
                        </div>
                        <div className="p-2 bg-[#C0C0C0]">
                          <UserActionDropdown 
                            user={onlineUsers.find(u => u.user_id === showUserMenu) || {}} 
                            position={{ x: 0, y: 0 }}
                            onClose={() => setShowUserMenu(null)}
                            isInModal={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pending Users Tab */}
              {activeTab === "pending" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    Pending Approval Requests
                  </h3>
                  
                  {pendingUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No pending registrations</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {pendingUsers.map((u) => (
                        <div 
                          key={u.user_id} 
                          className="retro-window p-3"
                          data-testid={`pending-user-${u.user_id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-sm flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: u.avatar_color }}
                              >
                                {u.username?.[0]?.toUpperCase()}
                              </div>
                              <div>
                                <p className="font-verdana text-sm font-bold">{u.username}</p>
                                <p className="font-arial text-xs text-[#000080]">{u.email}</p>
                                {u.recovery_email && (
                                  <p className="font-arial text-xs text-[#808080]">Recovery: {u.recovery_email}</p>
                                )}
                                <p className="font-arial text-xs text-[#808080]">
                                  Registered: {formatDate(u.created_at)}
                                </p>
                                {u.last_ip && (
                                  <p className="font-arial text-xs text-[#999]">IP: {u.last_ip}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              {/* Accept dropdown */}
                              {hasPermission("approve_users") && (
                              <div className="flex gap-1">
                                <select 
                                  className="retro-input text-xs py-1"
                                  value={approveType}
                                  onChange={(e) => setApproveType(e.target.value)}
                                >
                                  <option value="guest">Guest</option>
                                  <option value="temporary">Temporary</option>
                                  <option value="standard">Standard</option>
                                  <option value="vip">VIP</option>
                                </select>
                                <button 
                                  className="retro-button px-3 py-1 bg-green-100 flex items-center gap-1 text-xs"
                                  onClick={() => handleAction(u.user_id, `approve_${approveType}`)}
                                  data-testid={`approve-${u.user_id}`}
                                >
                                  <CheckCircle className="w-3 h-3 text-green-600" />
                                  Accept
                                </button>
                              </div>
                              )}
                              {hasPermission("deny_users") && (
                              <button 
                                className="retro-button px-3 py-1 bg-red-100 flex items-center gap-1 text-xs"
                                onClick={() => setSelectedUser({ ...u, action: 'deny' })}
                                data-testid={`deny-${u.user_id}`}
                              >
                                <XCircle className="w-3 h-3 text-red-600" />
                                Deny
                              </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Applications Tab */}
              {activeTab === "applications" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Team Applications ({applications.length})
                  </h3>
                  
                  {applications.length === 0 ? (
                    <div className="text-center py-8">
                      <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No pending applications</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {applications.map((app) => (
                        <div key={app.application_id} className="retro-window p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                                  app.application_type === 'admin' 
                                    ? 'bg-[#7B0099] text-white' 
                                    : 'bg-[#000080] text-white'
                                }`}>
                                  {app.application_type === 'admin' ? (
                                    <><Shield className="w-3 h-3 inline mr-1" />Admin</>
                                  ) : (
                                    <><Award className="w-3 h-3 inline mr-1" />Moderator</>
                                  )}
                                </span>
                                <span className="font-verdana text-sm font-bold">{app.username}</span>
                                <span className="text-[#808080] text-xs">({app.email})</span>
                              </div>
                              <div className="retro-inset p-2 mb-2">
                                <p className="font-arial text-xs whitespace-pre-wrap">{app.reason}</p>
                              </div>
                              <p className="font-arial text-[10px] text-[#808080]">
                                Submitted: {formatDate(app.submitted_at)}
                              </p>
                            </div>
                            <div className="flex flex-col gap-1">
                              <button 
                                className="retro-button px-3 py-1 bg-green-100 flex items-center gap-1 text-xs"
                                onClick={() => handleReviewApplication(app.application_id, 'approve')}
                                data-testid={`approve-app-${app.application_id}`}
                              >
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                Approve
                              </button>
                              <button 
                                className="retro-button px-3 py-1 bg-red-100 flex items-center gap-1 text-xs"
                                onClick={() => {
                                  const notes = prompt("Reason for denial (optional):");
                                  handleReviewApplication(app.application_id, 'deny', notes || '');
                                }}
                                data-testid={`deny-app-${app.application_id}`}
                              >
                                <XCircle className="w-3 h-3 text-red-600" />
                                Deny
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Room Access Requests Tab - Super Admin Only */}
              {activeTab === "room_access" && isSuperAdmin && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2">
                    Room Access Requests ({roomAccessRequests.length})
                  </h3>
                  
                  {roomAccessRequests.length === 0 ? (
                    <div className="retro-inset p-4 text-center">
                      <p className="font-arial text-sm text-[#808080]">No pending access requests</p>
                    </div>
                  ) : (
                    <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2 space-y-2">
                      {roomAccessRequests.map((req) => (
                        <div 
                          key={req.request_id}
                          className="retro-window p-2 bg-purple-50"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-verdana text-sm font-bold">{req.username}</p>
                              <p className="font-arial text-xs text-[#808080]">
                                Wants access to: <strong>{req.room_name}</strong>
                              </p>
                              <p className="font-arial text-xs text-[#808080]">
                                Requested: {new Date(req.requested_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                className="retro-button px-3 py-1 bg-green-100 flex items-center gap-1 text-xs"
                                onClick={() => handleRoomAccessRequest(req.request_id, true)}
                              >
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                Approve
                              </button>
                              <button
                                className="retro-button px-3 py-1 bg-red-100 flex items-center gap-1 text-xs"
                                onClick={() => handleRoomAccessRequest(req.request_id, false)}
                              >
                                <XCircle className="w-3 h-3 text-red-600" />
                                Deny
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* All Users Tab */}
              {activeTab === "users" && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {/* Category Filter Dropdown */}
                    <div className="flex items-center gap-1">
                      <select
                        value={userCategoryFilter}
                        onChange={(e) => setUserCategoryFilter(e.target.value)}
                        className="retro-button text-xs py-1 px-2 font-bold cursor-pointer bg-gradient-to-r from-purple-300 to-indigo-400"
                        data-testid="user-category-filter"
                      >
                        <option value="all">📋 All Users ({users.length})</option>
                        <option value="executive_admin">👑 Executive Admins ({users.filter(u => u.is_executive_admin || u.username === SUPER_ADMIN).length})</option>
                        <option value="admin">🛡️ Admins ({users.filter(u => u.is_admin && !u.is_executive_admin && u.username !== SUPER_ADMIN).length})</option>
                        <option value="moderator">🔵 Moderators ({users.filter(u => u.is_moderator && !u.is_admin).length})</option>
                        <option value="vip">⭐ VIPs ({users.filter(u => u.user_role === 'vip' && !u.is_moderator && !u.is_admin).length})</option>
                        <option value="elite_vip">💎 Elite VIPs ({users.filter(u => u.user_role === 'elite_vip' && !u.is_moderator && !u.is_admin).length})</option>
                      </select>
                    </div>
                    {/* Search Input */}
                    <div className="flex-1 flex items-center gap-1">
                      <Search className="w-4 h-4 text-[#808080]" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="retro-input flex-1 text-xs"
                        placeholder="Search users..."
                        data-testid="admin-search-input"
                      />
                    </div>
                    {/* Filtered Count */}
                    <span className="text-xs text-[#808080] self-center">
                      Showing: {filteredUsers.length}
                    </span>
                  </div>

                  <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar">
                    <table className="w-full text-xs">
                      <thead className="bg-[#000080] text-white sticky top-0">
                        <tr>
                          <th className="p-2 text-center w-10">Flag</th>
                          <th className="p-2 text-left">User</th>
                          <th className="p-2 text-left">Email</th>
                          <th className="p-2 text-left">IP</th>
                          <th className="p-2 text-center">Role</th>
                          <th className="p-2 text-center">Status</th>
                          <th className="p-2 text-center">Notes</th>
                          <th className="p-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => {
                          const roleInfo = getRoleDisplay(u);
                          const isSuperAdmin = u.username === SUPER_ADMIN;
                          return (
                            <tr 
                              key={u.user_id} 
                              className={`border-b border-[#E0E0E0] hover:bg-[#E0E0E0] ${u.is_banned ? 'bg-red-100' : ''} ${isSuperAdmin ? 'bg-purple-50' : ''}`}
                              data-testid={`admin-user-${u.user_id}`}
                            >
                              {/* Flag Column */}
                              <td className="p-2 text-center">
                                {currentUser?.username === SUPER_ADMIN ? (
                                  <div className="relative inline-block">
                                    <select
                                      className={`w-6 h-6 rounded cursor-pointer appearance-none ${getFlagColor(u.user_flag || 'green')}`}
                                      value={u.user_flag || 'green'}
                                      onChange={(e) => handleFlagChange(u.user_id, e.target.value)}
                                      title={`Flag: ${u.user_flag || 'green'}`}
                                    >
                                      <option value="green">🟢</option>
                                      <option value="yellow">🟡</option>
                                      <option value="red">🔴</option>
                                    </select>
                                  </div>
                                ) : (
                                  <span 
                                    className={`inline-block w-4 h-4 rounded-full ${getFlagColor(u.user_flag || 'green')}`}
                                    title={`Flag: ${u.user_flag || 'green'}`}
                                  />
                                )}
                              </td>
                              
                              {/* User Column */}
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  <UserRoleBadge role={u.user_role} isAdmin={u.is_admin} isModerator={u.is_moderator} />
                                  <span className={`${u.is_admin ? 'font-bold text-[#7B0099]' : ''} ${isSuperAdmin ? 'underline' : ''}`}>
                                    {u.username}
                                    {isSuperAdmin && <Crown className="w-3 h-3 inline ml-1 text-yellow-500" />}
                                  </span>
                                </div>
                              </td>
                              
                              {/* Email Column */}
                              <td className="p-2 text-[#808080]">{u.email}</td>
                              
                              {/* IP Column */}
                              <td className="p-2 text-[#999] font-mono text-[10px]">{u.last_ip || 'N/A'}</td>
                              
                              {/* Role Column - Fixed to show proper role names */}
                              <td className="p-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${roleInfo.color}`}>
                                  {roleInfo.text}
                                </span>
                              </td>
                              
                              {/* Status Column */}
                              <td className="p-2 text-center">
                                <span className={`inline-flex items-center gap-1 ${
                                  u.status === 'online' ? 'text-green-600' : 'text-[#808080]'
                                }`}>
                                  <span className={`w-2 h-2 rounded-full ${
                                    u.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                                  }`} />
                                </span>
                              </td>
                              
                              {/* Notes Column */}
                              <td className="p-2 text-center">
                                <button
                                  className="retro-button px-2 py-0.5 text-[10px] flex items-center gap-1 mx-auto"
                                  onClick={(e) => { e.stopPropagation(); handleOpenNotes(u); }}
                                  title="View/Add Notes"
                                >
                                  <StickyNote className="w-3 h-3" />
                                  Notes
                                </button>
                              </td>
                              
                              {/* Actions Column */}
                              <td className="p-2 text-center">
                                {canModifyUser(u) ? (
                                  <button
                                    className="retro-button px-2 py-0.5 text-[10px]"
                                    onClick={(e) => { e.stopPropagation(); handleUserClick(e, u); }}
                                  >
                                    Actions ▼
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-[#808080] italic">Protected</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Centered User Action Menu */}
                  {showUserMenu && canModifyUser(users.find(u => u.user_id === showUserMenu) || {}) && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowUserMenu(null)}>
                      <div className="retro-window w-64" onClick={e => e.stopPropagation()}>
                        <div className="retro-title-bar">
                          <span>User Actions</span>
                          <button className="close-button-visible" onClick={() => setShowUserMenu(null)}>✕</button>
                        </div>
                        <div className="p-2 bg-[#C0C0C0]">
                          <UserActionDropdown 
                            user={users.find(u => u.user_id === showUserMenu) || {}} 
                            position={{ x: 0, y: 0 }}
                            onClose={() => setShowUserMenu(null)}
                            isInModal={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Banned IPs Tab */}
              {activeTab === "banned" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                    <Ban className="w-4 h-4" />
                    Banned IP Addresses ({bannedIPs.length})
                  </h3>
                  
                  {/* Add new IP ban form */}
                  <div className="retro-window p-2 mb-4">
                    <p className="font-arial text-xs font-bold mb-2">Manually Ban an IP Address</p>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="font-arial text-xs text-[#808080]">IP Address</label>
                        <input
                          type="text"
                          value={newBanIP}
                          onChange={(e) => setNewBanIP(e.target.value)}
                          className="retro-input w-full text-xs"
                          placeholder="e.g., 192.168.1.1"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="font-arial text-xs text-[#808080]">Reason (optional)</label>
                        <input
                          type="text"
                          value={newBanReason}
                          onChange={(e) => setNewBanReason(e.target.value)}
                          className="retro-input w-full text-xs"
                          placeholder="Reason for ban"
                        />
                      </div>
                      <button 
                        className="retro-button px-3 py-1 bg-red-100 flex items-center gap-1 text-xs"
                        onClick={handleBanIP}
                      >
                        <Ban className="w-3 h-3 text-red-600" />
                        Ban IP
                      </button>
                    </div>
                  </div>
                  
                  {bannedIPs.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No banned IPs</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-[#800000] text-white sticky top-0">
                        <tr>
                          <th className="p-2 text-left">IP Address</th>
                          <th className="p-2 text-left">Associated Users</th>
                          <th className="p-2 text-left">Reason</th>
                          <th className="p-2 text-left">Banned By</th>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bannedIPs.map((b, i) => (
                          <tr key={i} className="border-b border-[#E0E0E0] hover:bg-[#FFE0E0]">
                            <td className="p-2 font-mono font-bold">{b.ip}</td>
                            <td className="p-2 text-[#000080]">
                              {b.associated_users?.join(', ') || 'Manual ban'}
                            </td>
                            <td className="p-2">{b.reason || 'No reason'}</td>
                            <td className="p-2">{b.banned_by}</td>
                            <td className="p-2 text-[#808080]">{formatDate(b.banned_at)}</td>
                            <td className="p-2 text-center">
                              <button 
                                className="retro-button px-2 py-0.5 bg-green-100 text-xs"
                                onClick={() => handleUnbanIP(b.ip)}
                              >
                                Unban
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Voice Bans Tab */}
              {activeTab === "voice_bans" && (
                <VoiceBansManager />
              )}

              {/* Denied Log Tab */}
              {activeTab === "denied" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Denied Registrations Log
                  </h3>
                  
                  {deniedLog.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No denied registrations</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-[#800000] text-white sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Username</th>
                          <th className="p-2 text-left">Email</th>
                          <th className="p-2 text-left">IP Addresses</th>
                          <th className="p-2 text-left">Reason</th>
                          <th className="p-2 text-left">Denied By</th>
                          <th className="p-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deniedLog.map((d, i) => (
                          <tr key={i} className="border-b border-[#E0E0E0] hover:bg-[#FFE0E0]">
                            <td className="p-2 font-bold">{d.username}</td>
                            <td className="p-2 text-[#000080]">{d.email}</td>
                            <td className="p-2 text-[#999] font-mono text-[10px]">
                              {d.ip_addresses?.join(', ') || d.last_ip || 'N/A'}
                            </td>
                            <td className="p-2">{d.reason || 'No reason'}</td>
                            <td className="p-2">{d.denied_by}</td>
                            <td className="p-2 text-[#808080]">{formatDate(d.denied_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Registration Log Tab */}
              {activeTab === "reglog" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Registration Log ({registrationLog.length})
                  </h3>
                  
                  {registrationLog.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No registration records</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-[#000080] text-white sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Username</th>
                          <th className="p-2 text-left">Email</th>
                          <th className="p-2 text-left">Recovery Email</th>
                          <th className="p-2 text-left">IP Address</th>
                          <th className="p-2 text-left">Location</th>
                          <th className="p-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrationLog.map((log, i) => (
                          <tr key={i} className="border-b border-[#E0E0E0] hover:bg-[#E0E0E0]">
                            <td className="p-2 font-bold">{log.username}</td>
                            <td className="p-2 text-[#000080]">{log.email}</td>
                            <td className="p-2 text-[#666]">{log.recovery_email || 'N/A'}</td>
                            <td className="p-2 text-[#999] font-mono text-[10px]">{log.ip || 'N/A'}</td>
                            <td className="p-2 text-[#666]">
                              {log.location ? `${log.location.city || 'Unknown'}, ${log.location.country || 'Unknown'}` : 'Unknown'}
                            </td>
                            <td className="p-2 text-[#808080]">{formatDate(log.registered_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Login Log Tab */}
              {activeTab === "login_log" && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-verdana text-sm font-bold text-green-700 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Login History ({loginHistory.length}) - Last 30 Days
                    </h3>
                    <button
                      className="retro-button text-xs px-3 py-1 bg-yellow-100 hover:bg-yellow-200 flex items-center gap-1"
                      onClick={async () => {
                        if (!window.confirm("Force-close any abandoned 'still online' sessions and reset ghost users? This is safe and only affects users with no active connection right now.")) return;
                        try {
                          const r = await axios.post(`${API}/admin/login-history/cleanup-stale`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(r.data?.message || "Cleanup complete");
                          // Refresh login history
                          const fresh = await axios.get(`${API}/admin/login-history?days=30`, { headers: { Authorization: `Bearer ${token}` } });
                          setLoginHistory(fresh.data?.login_history || []);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Cleanup failed");
                        }
                      }}
                      data-testid="cleanup-stale-sessions-btn"
                    >
                      🧹 Cleanup Stale Sessions
                    </button>
                  </div>
                  
                  {loginHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="font-arial text-sm text-[#808080]">No login records yet</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-green-700 text-white sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Username</th>
                          <th className="p-2 text-center">Via</th>
                          <th className="p-2 text-left">Login Time</th>
                          <th className="p-2 text-left">Logout Time</th>
                          <th className="p-2 text-center">Duration</th>
                          <th className="p-2 text-left">IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginHistory.map((log, i) => {
                          const viaMessenger = log.source === "messenger";
                          return (
                          <tr key={i} className={`border-b border-[#E0E0E0] ${viaMessenger ? 'bg-purple-50' : ''} ${log.currently_online ? 'bg-green-100' : log.stale ? 'bg-gray-50 text-gray-500' : 'hover:bg-green-50'}`} data-testid={`login-row-${i}`}>
                            <td className="p-2 font-bold">{log.username}</td>
                            <td className="p-2 text-center">
                              {viaMessenger ? (
                                <span className="inline-block px-2 py-0.5 rounded-full bg-purple-600 text-white text-[10px] font-bold" data-testid={`login-source-messenger-${i}`}>💬 MESSENGER</span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded-full bg-gray-300 text-gray-700 text-[10px] font-bold">CHAT</span>
                              )}
                            </td>
                            <td className="p-2 text-green-600">{formatDate(log.login_time)}</td>
                            <td className="p-2 text-red-600">
                              {log.currently_online
                                ? <span className="text-green-600 font-bold">🟢 Still Online</span>
                                : log.stale
                                  ? <span className="text-gray-500 italic">ended (auto-closed)</span>
                                  : formatDate(log.logout_time)}
                            </td>
                            <td className="p-2 text-center">
                              {log.session_duration !== null && log.session_duration !== undefined ? (
                                <span className="bg-blue-100 px-2 py-1 rounded">
                                  {log.session_duration >= 60 
                                    ? `${Math.floor(log.session_duration / 60)}h ${log.session_duration % 60}m`
                                    : `${log.session_duration}m`}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-2 text-[#999] font-mono text-[10px]">{log.ip_address || 'N/A'}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Stats Tab */}
              {activeTab === "stats" && stats && (
                <div className="retro-inset p-4 flex-1 overflow-y-auto">
                  <h3 className="font-verdana text-lg font-bold text-[#7B0099] mb-4">Site Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="retro-window p-3 text-center">
                      <Clock className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                      <p className="font-verdana text-2xl font-bold">{stats.pending_users}</p>
                      <p className="font-arial text-xs text-[#808080]">Pending</p>
                    </div>
                    <div className="retro-window p-3 text-center">
                      <Users className="w-6 h-6 mx-auto mb-2 text-[#000080]" />
                      <p className="font-verdana text-2xl font-bold">{stats.total_users}</p>
                      <p className="font-arial text-xs text-[#808080]">Total Users</p>
                    </div>
                    <div className="retro-window p-3 text-center">
                      <div className="w-6 h-6 mx-auto mb-2 bg-green-500 rounded-full" />
                      <p className="font-verdana text-2xl font-bold">{stats.online_users}</p>
                      <p className="font-arial text-xs text-[#808080]">Online Now</p>
                    </div>
                    <div className="retro-window p-3 text-center">
                      <Ban className="w-6 h-6 mx-auto mb-2 text-red-600" />
                      <p className="font-verdana text-2xl font-bold">{stats.banned_users}</p>
                      <p className="font-arial text-xs text-[#808080]">Banned</p>
                    </div>
                  </div>
                  
                  {/* Daily Login Stats */}
                  {dailyLoginStats && (
                    <div className="mt-4">
                      <h4 className="font-verdana font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Daily Login Statistics
                      </h4>
                      <div className="retro-window p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="font-arial text-sm">Today&apos;s Logins:</span>
                          <span className="font-bold text-lg text-green-600">
                            {dailyLoginStats.today?.unique_users || 0} unique / {dailyLoginStats.today?.total_logins || 0} total
                          </span>
                        </div>
                      </div>
                      <div className="retro-inset p-2 max-h-[200px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#808080] text-white sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Date</th>
                              <th className="p-2 text-center">Unique Users</th>
                              <th className="p-2 text-center">Total Logins</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dailyLoginStats.daily_stats?.map((day, idx) => (
                              <tr key={day.date} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F0F0F0]'}>
                                <td className="p-2">{day.date}</td>
                                <td className="p-2 text-center font-bold text-blue-600">{day.unique_users}</td>
                                <td className="p-2 text-center text-[#808080]">{day.total_logins}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* System Bot Tab */}
              {activeTab === "system_bot" && (
                <div className="retro-inset p-4 flex-1 overflow-y-auto">
                  <h3 className="font-verdana text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
                    🤖 System Bot - Site-Wide Broadcast
                  </h3>
                  
                  <div className="bg-yellow-50 border-2 border-yellow-400 p-3 mb-4 rounded">
                    <p className="font-arial text-sm text-yellow-800">
                      ⚠️ Messages sent here will appear in <strong>ALL active chat rooms</strong> as a System announcement.
                    </p>
                  </div>

                  {/* Compose Broadcast */}
                  <div className="retro-window p-4 mb-4">
                    <h4 className="font-verdana text-sm font-bold mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Compose Broadcast
                    </h4>
                    
                    {/* Preset Messages */}
                    <div className="mb-3">
                      <label className="font-arial text-xs text-[#808080] block mb-1">Quick Messages (click to broadcast):</label>
                      {systemBotPresets.length === 0 ? (
                        <p className="font-arial text-xs text-[#808080] italic">No presets configured. {isSuperAdmin ? 'Add some below!' : 'Ask site owner to add presets.'}</p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {systemBotPresets.map((preset) => (
                            <button
                              key={preset.preset_id}
                              className="retro-button text-xs px-3 py-1 bg-blue-100 hover:bg-blue-200"
                              onClick={() => setSystemBroadcastMessage(preset.content)}
                              title={preset.content}
                              data-testid={`preset-btn-${preset.preset_id}`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Custom Message Input */}
                    <div className="mb-3">
                      <label className="font-arial text-xs text-[#808080] block mb-1">Message to broadcast:</label>
                      <textarea
                        className="retro-inset w-full p-2 h-20 font-arial text-sm resize-none"
                        placeholder="Enter your system announcement..."
                        value={systemBroadcastMessage}
                        onChange={(e) => setSystemBroadcastMessage(e.target.value)}
                        maxLength={500}
                        data-testid="broadcast-message-input"
                      />
                      <p className="text-xs text-[#808080] text-right">{systemBroadcastMessage.length}/500</p>
                    </div>
                    
                    {/* Send Button */}
                    <button
                      className="retro-button px-6 py-2 bg-red-200 hover:bg-red-300 flex items-center gap-2"
                      onClick={sendSystemBroadcast}
                      disabled={sendingBroadcast || !systemBroadcastMessage.trim()}
                      data-testid="send-broadcast-btn"
                    >
                      {sendingBroadcast ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          📢 Send to All Rooms
                        </>
                      )}
                    </button>
                  </div>

                  {/* Manage Presets - Super Admin Only */}
                  {isSuperAdmin && (
                    <div className="retro-window p-4 mb-4 border-2 border-[#7B0099]">
                      <h4 className="font-verdana text-sm font-bold mb-3 flex items-center gap-2 text-[#7B0099]">
                        <Settings className="w-4 h-4" /> Manage Preset Messages (Site Owner Only)
                      </h4>
                      
                      {/* Add New Preset */}
                      <div className="mb-4 p-3 bg-gray-100 border border-gray-300">
                        <h5 className="font-arial text-xs font-bold mb-2">Add New Preset:</h5>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            className="retro-inset flex-1 p-1 text-xs"
                            placeholder="Button label (e.g. 'Restart Notice')"
                            value={newPresetLabel}
                            onChange={(e) => setNewPresetLabel(e.target.value)}
                            maxLength={30}
                            data-testid="new-preset-label"
                          />
                          <span className="text-xs text-[#808080]">{newPresetLabel.length}/30</span>
                        </div>
                        <textarea
                          className="retro-inset w-full p-1 h-16 text-xs resize-none mb-2"
                          placeholder="Message content (what will be broadcast)"
                          value={newPresetContent}
                          onChange={(e) => setNewPresetContent(e.target.value)}
                          maxLength={500}
                          data-testid="new-preset-content"
                        />
                        <button
                          className="retro-button text-xs px-4 py-1 bg-green-200"
                          onClick={createPreset}
                          disabled={!newPresetLabel.trim() || !newPresetContent.trim()}
                          data-testid="create-preset-btn"
                        >
                          + Add Preset
                        </button>
                      </div>
                      
                      {/* Existing Presets */}
                      <div className="space-y-2 max-h-48 overflow-y-auto retro-scrollbar">
                        {systemBotPresets.length === 0 ? (
                          <p className="text-xs text-[#808080] text-center py-2">No presets yet. Create one above!</p>
                        ) : (
                          systemBotPresets.map((preset) => (
                            <div key={preset.preset_id} className="retro-inset p-2">
                              {editingPreset?.preset_id === preset.preset_id ? (
                                <div>
                                  <input
                                    type="text"
                                    className="retro-inset w-full p-1 text-xs mb-1"
                                    value={editingPreset.label}
                                    onChange={(e) => setEditingPreset({...editingPreset, label: e.target.value})}
                                    maxLength={30}
                                  />
                                  <textarea
                                    className="retro-inset w-full p-1 h-12 text-xs resize-none mb-1"
                                    value={editingPreset.content}
                                    onChange={(e) => setEditingPreset({...editingPreset, content: e.target.value})}
                                    maxLength={500}
                                  />
                                  <div className="flex gap-1">
                                    <button className="retro-button text-xs px-2 bg-green-200" onClick={() => updatePreset(preset.preset_id)}>Save</button>
                                    <button className="retro-button text-xs px-2" onClick={() => setEditingPreset(null)}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="font-bold text-xs text-blue-600">{preset.label}</p>
                                    <p className="text-xs text-[#666] truncate">{preset.content}</p>
                                  </div>
                                  <div className="flex gap-1">
                                    <button 
                                      className="retro-button text-xs px-2"
                                      onClick={() => setEditingPreset({...preset})}
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      className="retro-button text-xs px-2 bg-red-200"
                                      onClick={() => deletePreset(preset.preset_id)}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Broadcast History */}
                  <div className="retro-window p-4">
                    <h4 className="font-verdana text-sm font-bold mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Recent Broadcasts ({broadcastHistory.length})
                    </h4>
                    
                    {broadcastHistory.length === 0 ? (
                      <p className="font-arial text-sm text-[#808080] text-center py-4">No broadcasts sent yet</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto retro-scrollbar">
                        {broadcastHistory.map((broadcast, idx) => (
                          <div key={broadcast.broadcast_id || idx} className="retro-inset p-2">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-arial text-xs text-[#808080]">
                                By: <strong>{broadcast.sent_by}</strong>
                              </span>
                              <span className="font-arial text-xs text-[#808080]">
                                {formatDate(broadcast.sent_at)}
                              </span>
                            </div>
                            <p className="font-arial text-sm">{broadcast.content}</p>
                            <p className="font-arial text-xs text-green-600 mt-1">
                              Sent to {broadcast.rooms_count || 0} rooms
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Radio Manager Tab */}
              {activeTab === "radio_manager" && (
                <div className="retro-inset flex-1 overflow-y-auto">
                  <AdminRadioManager />
                </div>
              )}

              {/* Vouch Users Tab - Super Admin Only */}
              {activeTab === "vouch" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-3">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    ✅ Vouch Users (Grant Adult Room Access)
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Vouch for trusted friends to grant them adult room access without age verification.
                  </p>

                  {/* Search to add new vouch */}
                  <div className="retro-window p-3 mb-4">
                    <h4 className="font-bold text-xs mb-2">Vouch a User</h4>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={vouchSearchQuery}
                        onChange={(e) => setVouchSearchQuery(e.target.value)}
                        className="retro-input flex-1 text-xs"
                        placeholder="Search username to vouch..."
                      />
                    </div>
                    {vouchSearchResults.length > 0 && (
                      <div className="space-y-1">
                        {vouchSearchResults.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div>
                              <span className="font-bold text-xs">{u.username}</span>
                              {u.is_gold_user && <span className="ml-1 text-yellow-600 text-[10px]">👑 Gold</span>}
                              {u.is_vip && <span className="ml-1 text-purple-600 text-[10px]">⭐ VIP</span>}
                            </div>
                            <button
                              className="retro-button text-[10px] px-2 py-0.5 bg-green-100"
                              onClick={() => vouchUser(u.user_id)}
                              disabled={vouchingUser}
                            >
                              ✅ Vouch
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {vouchSearchQuery && vouchSearchResults.length === 0 && (
                      <p className="text-xs text-gray-500">No users found or all are already vouched</p>
                    )}
                  </div>

                  {/* Vouched Users List */}
                  <div className="retro-window p-3">
                    <h4 className="font-bold text-xs mb-2 flex items-center justify-between">
                      <span>Vouched Users ({vouchedUsers.length})</span>
                      <button className="retro-button text-[10px] px-2" onClick={fetchVouchedUsers}>
                        Refresh
                      </button>
                    </h4>
                    {vouchedUsers.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-4">No vouched users yet</p>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto retro-scrollbar">
                        {vouchedUsers.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div>
                              <span className="font-bold text-xs">{u.username}</span>
                              {u.is_gold_user && <span className="ml-1 text-yellow-600 text-[10px]">👑</span>}
                              {u.is_gold_vip && <span className="ml-1 text-purple-600 text-[10px]">💎</span>}
                              <p className="text-[10px] text-gray-500">
                                Vouched by {u.vouched_by} on {new Date(u.vouched_at).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              className="retro-button text-[10px] px-2 py-0.5 bg-red-100 text-red-600"
                              onClick={() => revokeVouch(u.user_id)}
                            >
                              Revoke
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Elite VIP Tab - Super Admin Only */}
              {activeTab === "elite_vip" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-3">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    👑 Elite VIP List
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Your personal list of VIP friends with special perks: bypass room capacity, auto age-verification, special room access.
                  </p>

                  {/* Search to add new Elite VIP */}
                  <div className="retro-window p-3 mb-4 bg-gradient-to-r from-amber-50 to-yellow-50">
                    <h4 className="font-bold text-xs mb-2 flex items-center gap-2">
                      <span>👑</span> Add to Elite VIP List
                    </h4>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={eliteVIPSearchQuery}
                        onChange={(e) => setEliteVIPSearchQuery(e.target.value)}
                        className="retro-input flex-1 text-xs"
                        placeholder="Search username to add..."
                      />
                    </div>
                    {eliteVIPSearchResults.length > 0 && (
                      <div className="space-y-1">
                        {eliteVIPSearchResults.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between p-2 bg-white rounded border border-yellow-300">
                            <div>
                              <span className="font-bold text-xs">{u.username}</span>
                              {u.is_admin && <span className="ml-1 text-red-600 text-[10px]">🛡️ Admin</span>}
                              {u.is_moderator && <span className="ml-1 text-blue-600 text-[10px]">🔧 Mod</span>}
                            </div>
                            <button
                              className="retro-button text-[10px] px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-400 text-white font-bold"
                              onClick={() => addEliteVIP(u.username)}
                              disabled={addingEliteVIP}
                            >
                              👑 Add to Elite
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {eliteVIPSearchQuery && eliteVIPSearchResults.length === 0 && (
                      <p className="text-xs text-gray-500">No users found or already on Elite VIP list</p>
                    )}
                  </div>

                  {/* Elite VIP Users List */}
                  <div className="retro-window p-3">
                    <h4 className="font-bold text-xs mb-2 flex items-center justify-between">
                      <span>👑 Elite VIP Members ({eliteVIPUsers.length})</span>
                      <button className="retro-button text-[10px] px-2" onClick={fetchEliteVIPUsers}>
                        Refresh
                      </button>
                    </h4>
                    {eliteVIPUsers.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-4">No Elite VIP members yet</p>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto retro-scrollbar">
                        {eliteVIPUsers.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between p-2 bg-gradient-to-r from-yellow-50 to-amber-50 rounded border border-yellow-300">
                            <div>
                              <span className="font-bold text-xs flex items-center gap-1">
                                <span>👑</span> {u.username}
                              </span>
                              <p className="text-[10px] text-gray-500">
                                Added {new Date(u.added_at).toLocaleDateString()} • Memo {u.memo_seen ? 'seen ✅' : 'not seen ⏳'}
                              </p>
                            </div>
                            <button
                              className="retro-button text-[10px] px-2 py-0.5 bg-red-100 text-red-600"
                              onClick={() => removeEliteVIP(u.username)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Perks Info */}
                  <div className="retro-window p-3 mt-4 bg-gradient-to-r from-purple-50 to-pink-50">
                    <h4 className="font-bold text-xs mb-2">Elite VIP Perks</h4>
                    <ul className="text-[10px] text-gray-600 space-y-1 list-disc list-inside">
                      <li>Cannot be banned/kicked by staff (only you)</li>
                      <li>Can enter full rooms (bypass 60 user limit)</li>
                      <li>Access to VIP & Elite VIP lounges</li>
                      <li>Auto age-verified for adult rooms</li>
                      <li>Personal protection - report issues directly to you</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Petty Cash Box Tab - Super Admin Only */}
              {activeTab === "petty_cash" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-3">
                  <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-3 flex items-center gap-2">
                    💰 Petty Cash Box
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Collects: 1% of diamond sales, game losses, F-Chips purchases, tips to site, and donations.
                  </p>

                  {prizePool ? (
                    <>
                      {/* Balance Cards */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {/* Diamonds */}
                        <div className="retro-window p-3 bg-gradient-to-r from-cyan-100 to-blue-100">
                          <h4 className="font-bold text-[10px] mb-1">Diamonds</h4>
                          <div className="text-xl font-bold text-cyan-700">
                            💎 {prizePool.diamond_balance?.toLocaleString() || 0}
                          </div>
                          <p className="text-[10px] text-gray-500">
                            ${prizePool.diamond_balance_in_dollars?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        {/* F-Chips */}
                        <div className="retro-window p-3 bg-gradient-to-r from-green-100 to-emerald-100">
                          <h4 className="font-bold text-[10px] mb-1">F-Chips</h4>
                          <div className="text-xl font-bold text-green-700">
                            🎰 {prizePool.fchip_balance?.toLocaleString() || 0}
                          </div>
                          <p className="text-[10px] text-gray-500">Game currency</p>
                        </div>
                        {/* Cash */}
                        <div className="retro-window p-3 bg-gradient-to-r from-yellow-100 to-amber-100">
                          <h4 className="font-bold text-[10px] mb-1">Cash</h4>
                          <div className="text-xl font-bold text-amber-700">
                            💵 ${prizePool.cash_balance?.toFixed(2) || '0.00'}
                          </div>
                          <p className="text-[10px] text-gray-500">From purchases</p>
                        </div>
                      </div>

                      {/* Deposit Section */}
                      <div className="retro-window p-3 mb-4 bg-gradient-to-r from-green-50 to-emerald-50">
                        <h4 className="font-bold text-xs mb-2">💰 Deposit to Petty Cash</h4>
                        <div className="flex gap-2 mb-2 flex-wrap">
                          <select
                            value={depositCurrency}
                            onChange={(e) => setDepositCurrency(e.target.value)}
                            className="retro-input text-xs w-28"
                          >
                            <option value="diamonds">Diamonds</option>
                            <option value="fchips">F-Chips</option>
                            <option value="cash">Cash</option>
                          </select>
                          <input
                            type="number"
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.value)}
                            className="retro-input w-24 text-xs"
                            placeholder="Amount"
                          />
                          <input
                            type="text"
                            value={depositReason}
                            onChange={(e) => setDepositReason(e.target.value)}
                            className="retro-input flex-1 text-xs"
                            placeholder="Reason (optional)"
                          />
                          <button
                            className="retro-button text-xs px-3 bg-green-200 hover:bg-green-300"
                            onClick={depositToPettyCash}
                            disabled={depositing}
                          >
                            {depositing ? '...' : '+ Deposit'}
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-500">Add diamonds, F-chips, or cash to the petty cash box</p>
                      </div>

                      {/* Withdraw Section */}
                      <div className="retro-window p-3 mb-4">
                        <h4 className="font-bold text-xs mb-2">Withdraw from Petty Cash</h4>
                        <div className="flex gap-2 mb-2 flex-wrap">
                          <select
                            value={withdrawCurrency}
                            onChange={(e) => setWithdrawCurrency(e.target.value)}
                            className="retro-input text-xs w-28"
                          >
                            <option value="diamonds">Diamonds</option>
                            <option value="fchips">F-Chips</option>
                            <option value="cash">Cash</option>
                          </select>
                          <input
                            type="number"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            className="retro-input w-24 text-xs"
                            placeholder="Amount"
                          />
                          <input
                            type="text"
                            value={withdrawReason}
                            onChange={(e) => setWithdrawReason(e.target.value)}
                            className="retro-input flex-1 text-xs"
                            placeholder="Reason"
                          />
                          <button
                            className="retro-button text-xs px-3 bg-green-100"
                            onClick={withdrawFromPettyCash}
                            disabled={withdrawing}
                          >
                            {withdrawing ? '...' : 'Withdraw'}
                          </button>
                        </div>
                      </div>

                      {/* Set Yana Account */}
                      <div className="retro-window p-3 mb-4 bg-gradient-to-r from-pink-50 to-purple-50">
                        <h4 className="font-bold text-xs mb-2">❓ Ask Yana Room Settings</h4>
                        <p className="text-[10px] text-gray-600 mb-2">Set which user receives F-Chips from the Ask Yana room.</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={yanaAccountUsername}
                            onChange={(e) => setYanaAccountUsername(e.target.value)}
                            className="retro-input flex-1 text-xs"
                            placeholder="Username (e.g., Alice8888)"
                          />
                          <button
                            className="retro-button text-xs px-3 bg-purple-100"
                            onClick={setYanaAccount}
                            disabled={settingYanaAccount}
                          >
                            {settingYanaAccount ? '...' : 'Set Yana Account'}
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">Current: Alice8888 (default)</p>
                      </div>

                      {/* Recent Contributions */}
                      <div className="retro-window p-3 mb-4">
                        <h4 className="font-bold text-xs mb-2">Recent Contributions ({prizePool.total_contributions || 0} total)</h4>
                        <div className="max-h-48 overflow-y-auto retro-scrollbar">
                          {prizePool.recent_contributions?.length > 0 ? (
                            <div className="space-y-1">
                              {prizePool.recent_contributions.slice().reverse().map((c, i) => (
                                <div key={i} className="text-[10px] flex justify-between p-1.5 bg-gray-50 rounded border-l-2 border-green-400">
                                  <div>
                                    <span className="font-bold">{c.source}</span>
                                    {c.username && <span className="text-gray-500"> - {c.username}</span>}
                                    {c.description && <p className="text-gray-400">{c.description}</p>}
                                  </div>
                                  <span className="text-green-600 font-bold">
                                    +{c.amount?.toLocaleString()} {c.currency === 'diamonds' ? '💎' : c.currency === 'fchips' ? '🎰' : '💵'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 text-center">No contributions yet</p>
                          )}
                        </div>
                      </div>

                      {/* Withdrawal History */}
                      <div className="retro-window p-3">
                        <h4 className="font-bold text-xs mb-2">Withdrawal History</h4>
                        <div className="max-h-40 overflow-y-auto retro-scrollbar">
                          {prizePool.withdrawals?.length > 0 ? (
                            <div className="space-y-1">
                              {prizePool.withdrawals.slice().reverse().map((w, i) => (
                                <div key={i} className="text-[10px] p-1.5 bg-red-50 rounded border-l-2 border-red-400">
                                  <div className="flex justify-between">
                                    <span className="text-red-600 font-bold">
                                      -{w.amount?.toLocaleString()} {w.currency === 'diamonds' ? '💎' : w.currency === 'fchips' ? '🎰' : '💵'}
                                    </span>
                                    <span>{new Date(w.timestamp).toLocaleDateString()}</span>
                                  </div>
                                  <p className="text-gray-600">{w.reason}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 text-center">No withdrawals yet</p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading petty cash...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Messages Tab */}
              {activeTab === "contact_messages" && isSuperAdmin && (
                <div className="p-4 overflow-auto retro-scrollbar h-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg">📧 Contact Form Messages</h3>
                    {unreadContactCount > 0 && (
                      <button
                        className="retro-button text-xs px-3 bg-blue-200 hover:bg-blue-300"
                        onClick={markAllContactsRead}
                      >
                        Mark All Read ({unreadContactCount})
                      </button>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-4">
                    All emails are sent to: <strong>yafukkinhoo@gmail.com</strong>
                  </p>

                  {contactSubmissions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No contact messages yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contactSubmissions.map((submission) => (
                        <div 
                          key={submission.contact_id} 
                          className={`retro-window p-3 ${!submission.read ? 'bg-yellow-50 border-l-4 border-yellow-500' : ''}`}
                          onClick={() => !submission.read && markContactRead(submission.contact_id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-bold text-sm flex items-center gap-2">
                                {!submission.read && <span className="bg-red-500 text-white text-[10px] px-1 rounded">NEW</span>}
                                {submission.subject}
                              </h4>
                              <p className="text-xs text-gray-600">
                                From: <strong>{submission.name}</strong> ({submission.email})
                                {submission.username && <span className="text-purple-600"> — User: @{submission.username}</span>}
                              </p>
                            </div>
                            <span className="text-[10px] text-gray-400">
                              {new Date(submission.submitted_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="bg-white p-2 rounded border text-sm whitespace-pre-wrap">
                            {submission.message}
                          </div>
                          <div className="mt-2 flex gap-2">
                            <a 
                              href={`mailto:${submission.email}?subject=Re: ${submission.subject}`}
                              className="retro-button text-xs px-2 bg-green-200 hover:bg-green-300"
                            >
                              📧 Reply via Email
                            </a>
                            {!submission.read && (
                              <button
                                className="retro-button text-xs px-2 bg-gray-200 hover:bg-gray-300"
                                onClick={(e) => { e.stopPropagation(); markContactRead(submission.contact_id); }}
                              >
                                ✓ Mark Read
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Adult Employees Tab */}
              {activeTab === "adult_employees" && isSuperAdmin && (
                <div className="p-4 overflow-auto retro-scrollbar h-full">
                  <h3 className="font-bold text-lg mb-4">💋 Adult Room Employees</h3>
                  <p className="text-xs text-gray-600 mb-4">
                    Employees work in adult rooms with customizable tip menus. Only you can add/remove employees.
                  </p>

                  {/* Add Employee */}
                  <div className="retro-window p-3 mb-4 bg-pink-50">
                    <h4 className="font-bold text-sm mb-2">➕ Add New Employee</h4>
                    <input
                      type="text"
                      value={employeeSearchQuery}
                      onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                      placeholder="Search users by username..."
                      className="retro-input w-full text-xs mb-2"
                    />
                    {employeeSearchResults.length > 0 && (
                      <div className="bg-white border rounded max-h-40 overflow-y-auto">
                        {employeeSearchResults.map(u => (
                          <div
                            key={u.user_id}
                            className="p-2 hover:bg-pink-100 cursor-pointer flex justify-between items-center border-b last:border-b-0"
                            onClick={() => addAdultEmployee(u.user_id, u.username)}
                          >
                            <span className="text-xs">{u.username}</span>
                            <button className="text-xs text-pink-600 hover:text-pink-800">
                              + Add as Employee
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Employee List */}
                  <div className="retro-window p-3">
                    <h4 className="font-bold text-sm mb-2">Current Employees ({adultEmployees.length})</h4>
                    {adultEmployees.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No employees added yet</p>
                    ) : (
                      <div className="space-y-2">
                        {adultEmployees.map(emp => (
                          <div key={emp.employee_id} className="flex items-center justify-between p-2 bg-gradient-to-r from-pink-100 to-purple-100 rounded">
                            <div>
                              <div className="font-bold text-sm text-pink-800">{emp.display_name}</div>
                              <div className="text-xs text-gray-600">@{emp.username}</div>
                              <div className="text-[10px] text-gray-500">
                                💎 {emp.total_tips_received?.toLocaleString() || 0} earned • {emp.tip_count || 0} tips
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${emp.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                {emp.is_active ? '✓ Active' : 'Inactive'}
                              </span>
                              <button
                                onClick={() => removeAdultEmployee(emp.user_id)}
                                className="text-xs text-red-600 hover:text-red-800 px-2 py-1 hover:bg-red-100 rounded"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Movie Playlists Tab */}
              {activeTab === "movie_playlists" && isSuperAdmin && (
                <div className="p-4 overflow-auto retro-scrollbar h-full">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Film className="w-5 h-5" />
                    🎬 Movie Playlists Management
                  </h3>
                  <p className="text-xs text-gray-600 mb-4">
                    Create and manage movie playlists. Users will only see the 3 playlists you mark as "Featured".
                  </p>

                  {loadingPlaylists ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Loading playlists...</p>
                    </div>
                  ) : (
                    <>
                      {/* Create New Playlist */}
                      <div className="retro-window p-3 mb-4 bg-blue-50">
                        <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Create New Playlist
                        </h4>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newPlaylistName}
                            onChange={(e) => setNewPlaylistName(e.target.value)}
                            placeholder="Enter playlist name..."
                            className="retro-input flex-1 text-xs"
                            onKeyPress={(e) => e.key === 'Enter' && createMoviePlaylist()}
                          />
                          <button
                            onClick={createMoviePlaylist}
                            className="retro-button px-3 py-1 bg-blue-100 text-xs flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" />
                            Create
                          </button>
                        </div>
                      </div>

                      {/* Playlists List */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Left Column: Playlists */}
                        <div className="retro-window p-3">
                          <h4 className="font-bold text-sm mb-3 flex items-center justify-between">
                            <span>📋 Playlists ({moviePlaylists.length})</span>
                            <span className="text-xs text-gray-500">
                              Featured: {moviePlaylists.filter(p => p.is_featured).length}/3
                            </span>
                          </h4>
                          
                          {moviePlaylists.length === 0 ? (
                            <div className="text-center py-8">
                              <Film className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                              <p className="text-xs text-gray-500">No playlists created yet</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto retro-scrollbar">
                              {moviePlaylists.map(playlist => (
                                <div 
                                  key={playlist.playlist_id}
                                  className={`p-3 rounded border-2 cursor-pointer transition-all ${
                                    selectedPlaylist?.playlist_id === playlist.playlist_id
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                  }`}
                                  onClick={() => setSelectedPlaylist(playlist)}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-bold text-sm flex items-center gap-2">
                                      {playlist.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                                      {playlist.name}
                                    </h5>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          togglePlaylistFeatured(playlist.playlist_id, !playlist.is_featured);
                                        }}
                                        className={`text-xs px-2 py-1 rounded ${
                                          playlist.is_featured 
                                            ? 'bg-yellow-200 text-yellow-800 hover:bg-yellow-300' 
                                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                        }`}
                                        disabled={!playlist.is_featured && moviePlaylists.filter(p => p.is_featured).length >= 3}
                                      >
                                        {playlist.is_featured ? '⭐ Featured' : '☆ Feature'}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteMoviePlaylist(playlist.playlist_id);
                                        }}
                                        className="text-xs text-red-600 hover:text-red-800 px-2 py-1 hover:bg-red-100 rounded"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    📹 {playlist.videos?.length || 0} videos
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Created: {new Date(playlist.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Right Column: Selected Playlist Details */}
                        <div className="retro-window p-3 max-h-[500px] overflow-y-auto">
                          {selectedPlaylist ? (
                            <>
                              <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                                <Play className="w-4 h-4" />
                                {selectedPlaylist.name}
                                {selectedPlaylist.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                              </h4>

                              {/* Add Video Form */}
                              <div className="mb-4 p-2 bg-green-50 rounded">
                                <h5 className="font-bold text-xs mb-2">➕ Add Video</h5>
                                <input
                                  type="text"
                                  value={newVideoTitle}
                                  onChange={(e) => setNewVideoTitle(e.target.value)}
                                  placeholder="Video title..."
                                  className="retro-input w-full text-xs mb-2"
                                />
                                <input
                                  type="text"
                                  value={newVideoUrl}
                                  onChange={(e) => setNewVideoUrl(e.target.value)}
                                  placeholder="Video URL (YouTube, Vimeo, etc.)..."
                                  className="retro-input w-full text-xs mb-2"
                                />
                                <button
                                  onClick={() => addVideoToPlaylist(selectedPlaylist.playlist_id)}
                                  className="retro-button w-full text-xs bg-green-100"
                                >
                                  Add Video
                                </button>
                              </div>

                              {/* YouTube Import */}
                              <div className="mb-4 p-2 bg-red-50 rounded">
                                <h5 className="font-bold text-xs mb-2 flex items-center gap-1">
                                  <Youtube className="w-3 h-3" />
                                  Import YouTube Playlist
                                </h5>
                                <input
                                  type="text"
                                  value={youtubePlaylistUrl}
                                  onChange={(e) => setYoutubePlaylistUrl(e.target.value)}
                                  placeholder="YouTube playlist URL..."
                                  className="retro-input w-full text-xs mb-2"
                                />
                                <button
                                  onClick={() => importYouTubePlaylist(selectedPlaylist.playlist_id)}
                                  className="retro-button w-full text-xs bg-red-100"
                                >
                                  Import Playlist
                                </button>
                              </div>

                              {/* Videos List */}
                              <div>
                                <h5 className="font-bold text-xs mb-2 flex items-center justify-between">
                                  <span>📹 Videos ({selectedPlaylist.videos?.length || 0})</span>
                                  <span className="text-[10px] text-gray-500">
                                    Top 3 shown to users: {(selectedPlaylist.top_videos || []).length}/3
                                  </span>
                                </h5>
                                {!selectedPlaylist.videos || selectedPlaylist.videos.length === 0 ? (
                                  <p className="text-xs text-gray-500 text-center py-4">No videos in this playlist</p>
                                ) : (
                                  <div className="space-y-2 max-h-80 overflow-y-auto retro-scrollbar">
                                    {selectedPlaylist.videos.map((video, index) => {
                                      const isInTop3 = (selectedPlaylist.top_videos || []).includes(video.id);
                                      const topPosition = (selectedPlaylist.top_videos || []).indexOf(video.id) + 1;
                                      
                                      return (
                                        <div key={video.id || index} className={`p-2 rounded border flex items-start gap-2 ${isInTop3 ? 'bg-yellow-50 border-yellow-300' : 'bg-white'}`}>
                                          {/* Thumbnail */}
                                          <img 
                                            src={`https://img.youtube.com/vi/${video.id}/default.jpg`}
                                            alt=""
                                            className="w-16 h-12 object-cover rounded flex-shrink-0"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <div className="font-bold text-xs flex items-center gap-1">
                                              {isInTop3 && <span className="bg-yellow-500 text-white text-[9px] px-1 rounded">#{topPosition}</span>}
                                              {video.title}
                                            </div>
                                            <div className="text-[10px] text-gray-500">ID: {video.id}</div>
                                          </div>
                                          <div className="flex flex-col gap-1">
                                            <button
                                              onClick={() => {
                                                const currentTop = selectedPlaylist.top_videos || [];
                                                let newTop;
                                                if (isInTop3) {
                                                  // Remove from top 3
                                                  newTop = currentTop.filter(id => id !== video.id);
                                                } else if (currentTop.length < 3) {
                                                  // Add to top 3
                                                  newTop = [...currentTop, video.id];
                                                } else {
                                                  toast.error("Already have 3 top movies. Remove one first.");
                                                  return;
                                                }
                                                // Update top movies
                                                axios.post(`${API}/admin/movie-playlists/${selectedPlaylist.playlist_id}/top-movies`,
                                                  { top_videos: newTop },
                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                ).then(() => {
                                                  toast.success(isInTop3 ? "Removed from top 3" : "Added to top 3");
                                                  fetchMoviePlaylists();
                                                }).catch(err => toast.error("Failed to update"));
                                              }}
                                              className={`text-[10px] px-2 py-1 rounded ${isInTop3 ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200 text-gray-600'}`}
                                            >
                                              {isInTop3 ? '⭐ In Top 3' : '☆ Add to Top 3'}
                                            </button>
                                            <button
                                              onClick={() => removeVideoFromPlaylist(selectedPlaylist.playlist_id, video.id)}
                                              className="text-[10px] text-red-600 hover:text-red-800 px-2 py-1 hover:bg-red-100 rounded"
                                            >
                                              🗑️ Remove
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-8">
                              <Film className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                              <p className="text-xs text-gray-500">Select a playlist to view details</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Featured Playlists Info */}
                      <div className="retro-window p-3 mt-4 bg-yellow-50">
                        <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-500" />
                          Featured Playlists (User Visible)
                        </h4>
                        <p className="text-xs text-gray-600 mb-2">
                          Only featured playlists are visible to users in the Movie Room. You can feature up to 3 playlists.
                        </p>
                        {moviePlaylists.filter(p => p.is_featured).length === 0 ? (
                          <p className="text-xs text-orange-600">⚠️ No playlists are currently featured. Users won't see any playlists!</p>
                        ) : (
                          <div className="space-y-1">
                            {moviePlaylists.filter(p => p.is_featured).map((playlist, index) => (
                              <div key={playlist.playlist_id} className="text-xs flex items-center gap-2">
                                <span className="font-bold">{index + 1}.</span>
                                <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                <span>{playlist.name}</span>
                                <span className="text-gray-500">({playlist.videos?.length || 0} videos)</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Movie Suggestions (Wanted Movies Log) Tab */}
              {activeTab === "movie_suggestions" && isSuperAdmin && (
                <div className="space-y-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span>📝 Wanted Movies Log</span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      {/* Filter Buttons */}
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => setSuggestionFilter("all")}
                          className={`retro-button text-xs px-3 py-1 ${suggestionFilter === "all" ? "bg-blue-500 text-white" : ""}`}
                        >
                          All ({movieSuggestions.length})
                        </button>
                        <button
                          onClick={() => setSuggestionFilter("pending")}
                          className={`retro-button text-xs px-3 py-1 ${suggestionFilter === "pending" ? "bg-yellow-500 text-white" : ""}`}
                        >
                          Pending ({movieSuggestions.filter(s => s.status === "pending").length})
                        </button>
                        <button
                          onClick={() => setSuggestionFilter("approved")}
                          className={`retro-button text-xs px-3 py-1 ${suggestionFilter === "approved" ? "bg-green-500 text-white" : ""}`}
                        >
                          Approved ({movieSuggestions.filter(s => s.status === "approved").length})
                        </button>
                        <button
                          onClick={() => setSuggestionFilter("rejected")}
                          className={`retro-button text-xs px-3 py-1 ${suggestionFilter === "rejected" ? "bg-red-500 text-white" : ""}`}
                        >
                          Rejected ({movieSuggestions.filter(s => s.status === "rejected").length})
                        </button>
                      </div>

                      {loadingSuggestions ? (
                        <p className="text-center py-4">Loading suggestions...</p>
                      ) : movieSuggestions.length === 0 ? (
                        <div className="text-center py-8">
                          <Film className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500">No movie suggestions yet</p>
                          <p className="text-xs text-gray-400 mt-1">Users can suggest movies from the Movie Theater room</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {movieSuggestions
                            .filter(s => suggestionFilter === "all" || s.status === suggestionFilter)
                            .map(suggestion => (
                              <div 
                                key={suggestion.suggestion_id} 
                                className={`retro-window p-3 ${
                                  suggestion.status === "approved" ? "bg-green-50 border-green-300" :
                                  suggestion.status === "rejected" ? "bg-red-50 border-red-300" :
                                  "bg-white"
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-bold text-sm flex items-center gap-2">
                                      🎬 {suggestion.title}
                                      {suggestion.status === "pending" && (
                                        <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">PENDING</span>
                                      )}
                                      {suggestion.status === "approved" && (
                                        <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">APPROVED</span>
                                      )}
                                      {suggestion.status === "rejected" && (
                                        <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">REJECTED</span>
                                      )}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Suggested by: <strong>{suggestion.suggested_by}</strong> • {new Date(suggestion.suggested_at).toLocaleDateString()}
                                    </p>
                                    {suggestion.youtube_url && (
                                      <a 
                                        href={suggestion.youtube_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline mt-1 block"
                                      >
                                        🔗 {suggestion.youtube_url}
                                      </a>
                                    )}
                                    {suggestion.notes && (
                                      <p className="text-xs text-gray-600 mt-2 italic">"{suggestion.notes}"</p>
                                    )}
                                    {suggestion.admin_notes && (
                                      <p className="text-xs text-purple-600 mt-1">
                                        <strong>Admin note:</strong> {suggestion.admin_notes}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    {suggestion.status === "pending" && (
                                      <>
                                        <button
                                          onClick={() => updateSuggestionStatus(suggestion.suggestion_id, "approved")}
                                          className="retro-button text-xs px-2 py-1 bg-green-500 text-white"
                                        >
                                          ✓ Approve
                                        </button>
                                        <button
                                          onClick={() => updateSuggestionStatus(suggestion.suggestion_id, "rejected")}
                                          className="retro-button text-xs px-2 py-1 bg-red-500 text-white"
                                        >
                                          ✗ Reject
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => {
                                        if (window.confirm("Delete this suggestion?")) {
                                          deleteSuggestion(suggestion.suggestion_id);
                                        }
                                      }}
                                      className="retro-button text-xs px-2 py-1 bg-gray-500 text-white"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Monitored Rooms Tab */}
              {activeTab === "monitored_rooms" && isSuperAdmin && (
                <div className="space-y-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span>📡 Monitored Rooms - Live Chat Logs</span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      <p className="text-xs text-gray-600 mb-4">
                        Messages from Help Desk, Feedback & Suggestions, Emergency 911, and New Users Welcome rooms.
                        Reply to users via IM directly from here.
                      </p>

                      {/* Room Filter Buttons */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          onClick={() => setMonitoredRoomFilter("all")}
                          className={`retro-button text-xs px-3 py-1 ${monitoredRoomFilter === "all" ? "bg-blue-500 text-white" : ""}`}
                        >
                          All Rooms ({monitoredMessages.length})
                        </button>
                        <button
                          onClick={() => setMonitoredRoomFilter("help-desk")}
                          className={`retro-button text-xs px-3 py-1 ${monitoredRoomFilter === "help-desk" ? "bg-blue-500 text-white" : ""}`}
                        >
                          ❓ Help Desk {monitoredUnreadCounts['help-desk'] > 0 && `(${monitoredUnreadCounts['help-desk']})`}
                        </button>
                        <button
                          onClick={() => setMonitoredRoomFilter("feedback-suggestions")}
                          className={`retro-button text-xs px-3 py-1 ${monitoredRoomFilter === "feedback-suggestions" ? "bg-blue-500 text-white" : ""}`}
                        >
                          💬 Feedback {monitoredUnreadCounts['feedback-suggestions'] > 0 && `(${monitoredUnreadCounts['feedback-suggestions']})`}
                        </button>
                        <button
                          onClick={() => setMonitoredRoomFilter("emergency-911")}
                          className={`retro-button text-xs px-3 py-1 ${monitoredRoomFilter === "emergency-911" ? "bg-red-500 text-white" : ""}`}
                        >
                          🚨 Emergency {monitoredUnreadCounts['emergency-911'] > 0 && `(${monitoredUnreadCounts['emergency-911']})`}
                        </button>
                        <button
                          onClick={() => setMonitoredRoomFilter("new-users")}
                          className={`retro-button text-xs px-3 py-1 ${monitoredRoomFilter === "new-users" ? "bg-green-500 text-white" : ""}`}
                        >
                          👋 New Users {monitoredUnreadCounts['new-users'] > 0 && `(${monitoredUnreadCounts['new-users']})`}
                        </button>
                      </div>

                      {loadingMonitored ? (
                        <p className="text-center py-4">Loading messages...</p>
                      ) : monitoredMessages.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500">No messages from monitored rooms yet</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {monitoredMessages
                            .filter(m => monitoredRoomFilter === "all" || m.room_id === monitoredRoomFilter)
                            .map(msg => (
                              <div 
                                key={msg.log_id} 
                                className={`retro-window p-3 ${!msg.read ? "bg-yellow-50 border-yellow-400" : msg.replied ? "bg-green-50" : "bg-white"}`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                                        {msg.room_name}
                                      </span>
                                      <span className="font-bold text-sm">{msg.username}</span>
                                      {!msg.read && (
                                        <span className="text-xs bg-yellow-500 text-white px-1 rounded">NEW</span>
                                      )}
                                      {msg.replied && (
                                        <span className="text-xs bg-green-500 text-white px-1 rounded">REPLIED</span>
                                      )}
                                    </div>
                                    <p className="text-sm mt-1 break-words">{msg.message}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {new Date(msg.timestamp).toLocaleString()}
                                    </p>
                                    {msg.admin_reply && (
                                      <p className="text-xs text-green-600 mt-1 italic">
                                        ↳ Replied: "{msg.admin_reply}"
                                      </p>
                                    )}

                                    {/* Reply Form */}
                                    {replyingTo === msg.log_id && (
                                      <div className="mt-2 flex gap-2">
                                        <input
                                          type="text"
                                          value={replyText}
                                          onChange={(e) => setReplyText(e.target.value)}
                                          placeholder="Type your reply..."
                                          className="retro-input flex-1 text-xs"
                                          onKeyDown={(e) => e.key === 'Enter' && replyToMonitoredMessage(msg.log_id)}
                                        />
                                        <button
                                          onClick={() => replyToMonitoredMessage(msg.log_id)}
                                          className="retro-button text-xs bg-green-500 text-white"
                                        >
                                          Send
                                        </button>
                                        <button
                                          onClick={() => { setReplyingTo(null); setReplyText(""); }}
                                          className="retro-button text-xs"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex flex-col gap-1">
                                    {!msg.read && (
                                      <button
                                        onClick={() => markMonitoredRead(msg.log_id)}
                                        className="retro-button text-xs px-2 py-1"
                                      >
                                        ✓ Read
                                      </button>
                                    )}
                                    {!msg.replied && replyingTo !== msg.log_id && (
                                      <button
                                        onClick={() => { setReplyingTo(msg.log_id); setReplyText(""); }}
                                        className="retro-button text-xs px-2 py-1 bg-blue-500 text-white"
                                      >
                                        ↩ Reply
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Staff Management Tab */}
              {activeTab === "staff_management" && isSuperAdmin && (
                <div className="space-y-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span>👥 Staff Management - Tasks & Notes</span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      <p className="text-xs text-gray-600 mb-4">
                        Manage admins, moderators, and employees. Assign tasks and keep notes.
                      </p>

                      {loadingStaff ? (
                        <p className="text-center py-4">Loading staff...</p>
                      ) : staffList.length === 0 ? (
                        <p className="text-center py-4 text-gray-500">No staff members found</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Staff List */}
                          <div className="space-y-2">
                            <h3 className="font-bold text-sm">Staff Members ({staffList.length})</h3>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                              {staffList.map(staff => (
                                <div 
                                  key={staff.user_id}
                                  onClick={() => setSelectedStaff(staff)}
                                  className={`retro-window p-2 cursor-pointer ${selectedStaff?.user_id === staff.user_id ? 'border-2 border-blue-500 bg-blue-50' : 'hover:bg-gray-100'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      staff.role === 'owner' ? 'bg-purple-600 text-white' :
                                      staff.role === 'admin' ? 'bg-red-500 text-white' :
                                      staff.role === 'moderator' ? 'bg-blue-500 text-white' :
                                      'bg-pink-500 text-white'
                                    }`}>
                                      {staff.role?.toUpperCase()}
                                    </span>
                                    <span className="font-bold">{staff.username}</span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {staff.tasks?.filter(t => t.status !== 'completed').length || 0} pending tasks
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Selected Staff Details */}
                          <div className="space-y-3">
                            {selectedStaff ? (
                              <>
                                <div className="retro-window p-3 bg-white">
                                  <h3 className="font-bold text-lg">{selectedStaff.username}</h3>
                                  <p className="text-xs text-gray-500">{selectedStaff.email}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded inline-block mt-1 ${
                                    selectedStaff.role === 'owner' ? 'bg-purple-600 text-white' :
                                    selectedStaff.role === 'admin' ? 'bg-red-500 text-white' :
                                    selectedStaff.role === 'moderator' ? 'bg-blue-500 text-white' :
                                    'bg-pink-500 text-white'
                                  }`}>
                                    {selectedStaff.role?.toUpperCase()}
                                  </span>
                                </div>

                                {/* Add Task Form */}
                                <div className="retro-window p-3">
                                  <h4 className="font-bold text-sm mb-2">📋 Add Task</h4>
                                  <input
                                    type="text"
                                    placeholder="Task title..."
                                    value={newTaskTitle}
                                    onChange={(e) => setNewTaskTitle(e.target.value)}
                                    className="retro-input w-full text-xs mb-2"
                                  />
                                  <textarea
                                    placeholder="Description (optional)..."
                                    value={newTaskDesc}
                                    onChange={(e) => setNewTaskDesc(e.target.value)}
                                    className="retro-input w-full text-xs mb-2 h-16 resize-none"
                                  />
                                  <div className="flex gap-2">
                                    <select
                                      value={newTaskPriority}
                                      onChange={(e) => setNewTaskPriority(e.target.value)}
                                      className="retro-input text-xs"
                                    >
                                      <option value="low">Low</option>
                                      <option value="normal">Normal</option>
                                      <option value="high">High</option>
                                      <option value="urgent">🔴 Urgent</option>
                                    </select>
                                    <button
                                      onClick={() => addStaffTask(selectedStaff.user_id)}
                                      className="retro-button text-xs bg-green-500 text-white flex-1"
                                    >
                                      Add Task
                                    </button>
                                  </div>
                                </div>

                                {/* Tasks List */}
                                <div className="retro-window p-3">
                                  <h4 className="font-bold text-sm mb-2">📋 Tasks ({selectedStaff.tasks?.length || 0})</h4>
                                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                    {selectedStaff.tasks?.length === 0 ? (
                                      <p className="text-xs text-gray-500">No tasks assigned</p>
                                    ) : (
                                      selectedStaff.tasks?.map(task => (
                                        <div key={task.task_id} className={`p-2 rounded text-xs ${
                                          task.status === 'completed' ? 'bg-green-100' :
                                          task.priority === 'urgent' ? 'bg-red-100' :
                                          task.priority === 'high' ? 'bg-orange-100' :
                                          'bg-gray-100'
                                        }`}>
                                          <div className="flex items-center justify-between">
                                            <span className={`font-bold ${task.status === 'completed' ? 'line-through' : ''}`}>
                                              {task.title}
                                            </span>
                                            <span className={`px-1 rounded text-[10px] ${
                                              task.status === 'completed' ? 'bg-green-500 text-white' :
                                              task.status === 'in_progress' ? 'bg-blue-500 text-white' :
                                              'bg-gray-300'
                                            }`}>
                                              {task.status}
                                            </span>
                                          </div>
                                          {task.description && <p className="text-gray-600 mt-1">{task.description}</p>}
                                          <div className="flex gap-1 mt-2">
                                            {task.status !== 'completed' && (
                                              <>
                                                <button onClick={() => updateTaskStatus(task.task_id, 'in_progress')} className="retro-button text-[10px] px-1">▶ Start</button>
                                                <button onClick={() => updateTaskStatus(task.task_id, 'completed')} className="retro-button text-[10px] px-1 bg-green-500 text-white">✓ Done</button>
                                              </>
                                            )}
                                            <button onClick={() => deleteTask(task.task_id)} className="retro-button text-[10px] px-1 bg-red-500 text-white">🗑️</button>
                                          </div>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>

                                {/* Notes Section */}
                                <div className="retro-window p-3">
                                  <h4 className="font-bold text-sm mb-2">📝 Notes</h4>
                                  <div className="flex gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Add a note..."
                                      value={newNote}
                                      onChange={(e) => setNewNote(e.target.value)}
                                      className="retro-input flex-1 text-xs"
                                      onKeyDown={(e) => e.key === 'Enter' && addStaffNote(selectedStaff.user_id)}
                                    />
                                    <button
                                      onClick={() => addStaffNote(selectedStaff.user_id)}
                                      className="retro-button text-xs bg-blue-500 text-white"
                                    >
                                      Add
                                    </button>
                                  </div>
                                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                                    {selectedStaff.notes?.length === 0 ? (
                                      <p className="text-xs text-gray-500">No notes yet</p>
                                    ) : (
                                      selectedStaff.notes?.map(note => (
                                        <div key={note.note_id} className="p-2 bg-yellow-50 rounded text-xs flex justify-between">
                                          <div>
                                            <p>{note.content}</p>
                                            <p className="text-[10px] text-gray-400">{note.created_by} - {new Date(note.created_at).toLocaleDateString()}</p>
                                          </div>
                                          <button onClick={() => deleteNote(note.note_id)} className="text-red-500 hover:text-red-700">×</button>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>Select a staff member to manage</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Staff Timecards Tab */}
              {activeTab === "staff_timecards" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar">
                  <div className="p-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span>⏱️ Staff Timecards - Login/Logout Hours</span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      <p className="text-xs text-gray-600 mb-4">
                        Track staff login times, logout times, and total hours worked like a timecard.
                      </p>

                      {loadingTimecard ? (
                        <p className="text-center py-4">Loading timecards...</p>
                      ) : !timecardData ? (
                        <p className="text-center py-4 text-gray-500">No timecard data available</p>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Staff Summary */}
                          <div className="space-y-2">
                            <h3 className="font-bold text-sm">Staff Hours Summary</h3>
                            <div className="space-y-2 max-h-[50vh] overflow-y-auto retro-scrollbar">
                              {timecardData.timecards?.map(staff => (
                                <div 
                                  key={staff.user_id}
                                  onClick={() => { fetchIndividualTimecard(staff.user_id, timecardPeriod); }}
                                  className={`retro-window p-2 cursor-pointer ${selectedTimecardStaff?.staff_id === staff.user_id ? 'border-2 border-blue-500 bg-blue-50' : 'hover:bg-gray-100'}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-bold">{staff.username}</span>
                                    <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">
                                      {staff.total_hours} hrs
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {staff.total_sessions} sessions • {staff.total_minutes} minutes total
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Individual Timecard Details */}
                          <div className="space-y-3">
                            {selectedTimecardStaff ? (
                              <>
                                <div className="retro-window p-3 bg-white">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h3 className="font-bold text-lg">{selectedTimecardStaff.username}</h3>
                                      <p className="text-xs text-gray-500">{selectedTimecardStaff.role}</p>
                                    </div>
                                    <select
                                      value={timecardPeriod}
                                      onChange={(e) => { setTimecardPeriod(e.target.value); fetchIndividualTimecard(selectedTimecardStaff.staff_id, e.target.value); }}
                                      className="retro-input text-xs"
                                    >
                                      <option value="day">Today</option>
                                      <option value="week">This Week</option>
                                      <option value="month">This Month</option>
                                      <option value="all">All Time</option>
                                    </select>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="bg-blue-100 p-2 rounded">
                                      <div className="text-lg font-bold text-blue-600">{selectedTimecardStaff.total_sessions}</div>
                                      <div className="text-[10px]">Sessions</div>
                                    </div>
                                    <div className="bg-green-100 p-2 rounded">
                                      <div className="text-lg font-bold text-green-600">{selectedTimecardStaff.total_hours}</div>
                                      <div className="text-[10px]">Hours</div>
                                    </div>
                                    <div className="bg-purple-100 p-2 rounded">
                                      <div className="text-lg font-bold text-purple-600">{selectedTimecardStaff.total_minutes}</div>
                                      <div className="text-[10px]">Minutes</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Daily Breakdown */}
                                <div className="retro-window p-3">
                                  <h4 className="font-bold text-sm mb-2">📅 Daily Breakdown</h4>
                                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                                    {selectedTimecardStaff.daily_breakdown?.length === 0 ? (
                                      <p className="text-xs text-gray-500">No data for this period</p>
                                    ) : (
                                      selectedTimecardStaff.daily_breakdown?.map(day => (
                                        <div key={day.date} className="flex justify-between text-xs p-1 bg-gray-100 rounded">
                                          <span>{day.date}</span>
                                          <span>{day.sessions} sessions • {Math.round(day.minutes / 60 * 10) / 10} hrs</span>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>

                                {/* Session Log */}
                                <div className="retro-window p-3">
                                  <h4 className="font-bold text-sm mb-2">🕐 Session Log</h4>
                                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                    {selectedTimecardStaff.sessions?.slice(0, 20).map((session, i) => (
                                      <div key={i} className="text-xs p-2 bg-gray-50 rounded border-l-2 border-green-500">
                                        <div className="flex justify-between">
                                          <span className="text-green-600">▶ IN: {new Date(session.login_time).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-red-600">◼ OUT: {session.logout_time ? new Date(session.logout_time).toLocaleString() : 'Still Online'}</span>
                                          <span className="font-bold">{session.duration_minutes ? `${Math.round(session.duration_minutes / 60 * 10) / 10} hrs` : '-'}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>Select a staff member to view timecard</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>
              )}

              {/* Admin Announcements Tab */}
              {activeTab === "admin_announcements" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar">
                  <div className="p-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span>📢 Admin Announcements - Mandatory Read</span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      <p className="text-xs text-gray-600 mb-4">
                        Send announcements to users. They must check a box to confirm they've read it.
                      </p>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Send New Announcement */}
                        <div className="retro-window p-3 bg-white">
                          <h4 className="font-bold text-sm mb-3">📣 Send New Announcement</h4>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs font-bold">Subject:</label>
                              <input
                                type="text"
                                value={announcementSubject}
                                onChange={(e) => setAnnouncementSubject(e.target.value)}
                                placeholder="Announcement subject..."
                                className="retro-input w-full text-xs mt-1"
                              />
                            </div>
                            
                            <div>
                              <label className="text-xs font-bold">Message:</label>
                              <textarea
                                value={announcementContent}
                                onChange={(e) => setAnnouncementContent(e.target.value)}
                                placeholder="Type your announcement..."
                                className="retro-input w-full text-xs mt-1 h-24 resize-none"
                              />
                            </div>
                            
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="text-xs font-bold">Priority:</label>
                                <select
                                  value={announcementPriority}
                                  onChange={(e) => setAnnouncementPriority(e.target.value)}
                                  className="retro-input w-full text-xs mt-1"
                                >
                                  <option value="normal">Normal</option>
                                  <option value="important">Important</option>
                                  <option value="urgent">🔴 Urgent</option>
                                </select>
                              </div>
                              <div className="flex-1">
                                <label className="text-xs font-bold">Recipients:</label>
                                <select
                                  value={announcementTargets}
                                  onChange={(e) => setAnnouncementTargets(e.target.value)}
                                  className="retro-input w-full text-xs mt-1"
                                >
                                  <option value="all">All Users</option>
                                  <option value="selected">Selected Users</option>
                                </select>
                              </div>
                            </div>
                            
                            {announcementTargets === "selected" && (
                              <div>
                                <label className="text-xs font-bold">Select Users ({selectedAnnouncementUsers.length} selected):</label>
                                <div className="retro-inset max-h-32 overflow-y-auto mt-1 p-1">
                                  {users.slice(0, 100).map(u => (
                                    <label key={u.user_id} className="flex items-center gap-1 text-xs py-0.5 hover:bg-gray-100 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selectedAnnouncementUsers.includes(u.user_id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedAnnouncementUsers(prev => [...prev, u.user_id]);
                                          } else {
                                            setSelectedAnnouncementUsers(prev => prev.filter(id => id !== u.user_id));
                                          }
                                        }}
                                      />
                                      {u.username}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <button
                              onClick={sendAnnouncement}
                              disabled={sendingAnnouncement || !announcementSubject.trim() || !announcementContent.trim()}
                              className="retro-button w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white disabled:opacity-50"
                            >
                              {sendingAnnouncement ? "Sending..." : "📢 Send Announcement"}
                            </button>
                          </div>
                        </div>

                        {/* Announcements List */}
                        <div className="retro-window p-3 bg-white">
                          <h4 className="font-bold text-sm mb-3">📋 Sent Announcements ({announcementsList.length})</h4>
                          
                          <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {announcementsList.length === 0 ? (
                              <p className="text-xs text-gray-500 text-center py-4">No announcements sent yet</p>
                            ) : (
                              announcementsList.map(ann => (
                                <div key={ann.announcement_id} className={`p-2 rounded text-xs border-l-4 ${
                                  ann.priority === 'urgent' ? 'bg-red-50 border-red-500' :
                                  ann.priority === 'important' ? 'bg-orange-50 border-orange-500' :
                                  'bg-gray-50 border-gray-300'
                                }`}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-bold">{ann.subject}</div>
                                      <p className="text-gray-600 mt-1 line-clamp-2">{ann.content}</p>
                                      <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-500">
                                        <span>{new Date(ann.created_at).toLocaleString()}</span>
                                        <span>•</span>
                                        <span className={ann.read_percentage === 100 ? 'text-green-600' : ''}>
                                          {ann.total_confirmed}/{ann.total_targets} read ({ann.read_percentage}%)
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => deleteAnnouncement(ann.announcement_id)}
                                      className="text-red-500 hover:text-red-700 p-1"
                                    >
                                      ×
                                    </button>
                                  </div>
                                  {/* Progress bar */}
                                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                    <div 
                                      className="bg-green-500 h-1.5 rounded-full transition-all"
                                      style={{ width: `${ann.read_percentage}%` }}
                                    />
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              )}

              {/* Moderation Log Tab */}
              {activeTab === "moderation_log" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar">
                  <div className="p-4">
                  <div className="retro-window">
                    <div className="retro-title-bar">
                      <span className="flex items-center gap-2">
                        <Gavel className="w-4 h-4" />
                        ⚖️ Moderation Log & Pending Approvals
                      </span>
                    </div>
                    <div className="p-4 bg-[#C0C0C0]">
                      <p className="text-xs text-gray-600 mb-4">
                        Review and approve moderation actions requested by moderators and employees. 
                        All actions are logged for accountability.
                      </p>
                      <ModerationLogPanel />
                    </div>
                  </div>
                  </div>
                </div>
              )}

              {/* Age Verification Tab */}
              {activeTab === "age_verifications" && isSuperAdmin && (
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar" data-testid="age-verifications-tab">
                  <div className="p-4">
                    <div className="retro-window">
                      <div className="retro-title-bar">
                        <span className="flex items-center gap-2">
                          🔞 Age Verification Queue
                        </span>
                      </div>
                      <div className="p-4 bg-[#C0C0C0] max-h-[65vh] overflow-y-auto">
                        <p className="text-xs text-gray-700 mb-3">
                          Users below have requested access to age-restricted (adult) rooms. Approve to unlock adult rooms for them; deny to clear the flag.
                        </p>
                        {loadingAgeVerifications ? (
                          <p className="text-sm">Loading...</p>
                        ) : pendingAgeVerifications.length === 0 ? (
                          <div className="text-center py-10 text-gray-600">
                            <p className="text-sm">No pending age verification requests.</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {pendingAgeVerifications.map((u) => (
                              <div key={u.user_id} className="retro-window p-3 bg-white flex items-center justify-between" data-testid={`age-verify-row-${u.user_id}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm text-[#000080]">{u.username}</p>
                                  <p className="text-[11px] text-gray-500 truncate">{u.email}</p>
                                  <p className="text-[10px] text-gray-400">
                                    Requested: {u.age_verification_requested_at ? new Date(u.age_verification_requested_at).toLocaleString() : 'unknown'}
                                  </p>
                                </div>
                                <div className="flex gap-2 ml-3">
                                  <button
                                    className="retro-button text-xs px-3 py-1 bg-green-500 text-white border-green-700"
                                    onClick={() => approveAgeVerification(u.user_id, u.username)}
                                    data-testid={`age-approve-${u.user_id}`}
                                  >
                                    ✓ Approve
                                  </button>
                                  <button
                                    className="retro-button text-xs px-3 py-1 bg-red-500 text-white border-red-700"
                                    onClick={() => denyAgeVerification(u.user_id, u.username)}
                                    data-testid={`age-deny-${u.user_id}`}
                                  >
                                    ✗ Deny
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Households / Family Mode Tab */}
          {activeTab === 'households' && isSuperAdmin && (
            <div className="retro-window">
              <div className="retro-title-bar">
                <span className="flex items-center gap-1">
                  🏠 Households / Family Mode
                </span>
              </div>
              <div className="bg-[#C0C0C0] max-h-[75vh] overflow-y-auto">
                <AdminHouseholdsPanel />
              </div>
            </div>
          )}

          {/* Voting System Tab */}
          {activeTab === 'voting_system' && isSuperAdmin && (
            <div className="retro-window">
              <div className="retro-title-bar">
                <span className="flex items-center gap-1">
                  <Trophy className="w-4 h-4" />
                  🏆 Voting System Management
                </span>
              </div>
              <div className="p-4 bg-[#C0C0C0] max-h-[70vh] overflow-y-auto">
                {/* Create New Poll */}
                <div className="retro-window p-3 mb-4">
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    Create New Poll
                  </h4>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Poll Title (e.g., User of the Month)"
                      value={newPollTitle}
                      onChange={(e) => setNewPollTitle(e.target.value)}
                      className="retro-input w-full text-sm"
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={newPollDescription}
                      onChange={(e) => setNewPollDescription(e.target.value)}
                      className="retro-input w-full text-sm"
                      rows={2}
                    />
                    
                    {/* Poll Type Selection */}
                    <div>
                      <label className="text-xs font-bold block mb-1">Poll Type:</label>
                      <select
                        value={newPollType}
                        onChange={(e) => setNewPollType(e.target.value)}
                        className="retro-input w-full text-sm"
                      >
                        <option value="text">📝 Text Input (Users type a username to vote)</option>
                        <option value="single_choice">🔘 Single Choice (Select one option - Radio buttons)</option>
                        <option value="multiple_choice">☑️ Multiple Choice (Select multiple - Checkboxes)</option>
                      </select>
                    </div>
                    
                    {/* Options for choice-based polls */}
                    {(newPollType === "single_choice" || newPollType === "multiple_choice") && (
                      <div className="bg-gray-100 p-3 rounded">
                        <label className="text-xs font-bold block mb-2">
                          Poll Options ({newPollOptions.length}/10):
                        </label>
                        <div className="space-y-2">
                          {newPollOptions.map((option, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                placeholder={`Option ${index + 1}`}
                                value={option}
                                onChange={(e) => updatePollOption(index, e.target.value)}
                                className="retro-input flex-1 text-sm"
                              />
                              {newPollOptions.length > 2 && (
                                <button
                                  onClick={() => removePollOption(index)}
                                  className="retro-button text-xs bg-red-500 text-white px-2"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {newPollOptions.length < 10 && (
                          <button
                            onClick={addPollOption}
                            className="retro-button text-xs mt-2 bg-blue-500 text-white"
                          >
                            + Add Option
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Expiry Date */}
                    <div>
                      <label className="text-xs font-bold block mb-1">Expiry Date (optional):</label>
                      <input
                        type="datetime-local"
                        value={newPollExpiry}
                        onChange={(e) => setNewPollExpiry(e.target.value)}
                        className="retro-input w-full text-sm"
                      />
                    </div>
                    
                    <button
                      onClick={createVotingPoll}
                      className="retro-button bg-green-500 text-white w-full"
                      disabled={!newPollTitle.trim()}
                    >
                      Create Poll
                    </button>
                  </div>
                </div>

                {/* Existing Polls */}
                <div className="retro-window p-3">
                  <h4 className="font-bold text-sm mb-2">📊 All Polls</h4>
                  {loadingPolls ? (
                    <p className="text-xs text-gray-500">Loading...</p>
                  ) : votingPolls.length === 0 ? (
                    <p className="text-xs text-gray-500">No polls created yet</p>
                  ) : (
                    <div className="space-y-2">
                      {votingPolls.map(poll => (
                        <div key={poll.poll_id} className={`p-2 rounded ${poll.is_active ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-100'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-bold text-sm">{poll.title}</span>
                              {poll.is_active && <span className="ml-2 text-xs bg-green-500 text-white px-1 rounded">ACTIVE</span>}
                            </div>
                            <button
                              onClick={() => togglePollActive(poll.poll_id, poll.is_active)}
                              className={`retro-button text-xs ${poll.is_active ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}
                            >
                              {poll.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                          {poll.description && <p className="text-xs text-gray-600 mt-1">{poll.description}</p>}
                          <p className="text-xs text-gray-400 mt-1">Created: {new Date(poll.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Room Widgets / Embedded Apps Tab */}
          {activeTab === 'room_widgets' && isSuperAdmin && (
            <div className="retro-window">
              <div className="retro-title-bar">
                <span className="flex items-center gap-1">🧩 Room Widgets / Embedded Apps</span>
              </div>
              <div className="bg-[#C0C0C0] max-h-[70vh] overflow-y-auto">
                <RoomWidgetControls token={token} />
              </div>
            </div>
          )}


          {/* Ask Rooms Tab */}
          {activeTab === 'ask_rooms' && isSuperAdmin && (
            <div className="retro-window">
              <div className="retro-title-bar">
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  💬 Ask Room Hosts Management
                </span>
              </div>
              <div className="p-4 bg-[#C0C0C0] max-h-[70vh] overflow-y-auto">
                <p className="text-xs text-gray-600 mb-3">
                  Assign users to host the adult "Ask" rooms. The host receives all entry fees and question payments.
                </p>
                
                <div className="space-y-3">
                  {ASK_ROOM_IDS.map(room => (
                    <div key={room.id} className="retro-window p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm">{room.name}</span>
                        {askRoomHosts[room.id] && (
                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">
                            Host: {askRoomHosts[room.id]}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter username..."
                          value={askRoomHostInput[room.id] || ""}
                          onChange={(e) => setAskRoomHostInput(prev => ({ ...prev, [room.id]: e.target.value }))}
                          className="retro-input flex-1 text-xs"
                        />
                        <button
                          onClick={() => setAskRoomHost(room.id)}
                          className="retro-button text-xs bg-blue-500 text-white"
                        >
                          Set Host
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Confirmation Modal */}
        {selectedUser && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="retro-window w-80">
              <div className="retro-title-bar">
                <span className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Confirm Action
                </span>
              </div>
              <div className="p-4 bg-[#C0C0C0]">
                <p className="font-arial text-sm mb-4">
                  Are you sure you want to <strong>{selectedUser.action}</strong> user <strong>{selectedUser.username}</strong>?
                </p>
                
                {(selectedUser.action === 'ban' || selectedUser.action === 'kick' || selectedUser.action === 'deny') && (
                  <div className="mb-4">
                    <label className="block font-arial text-xs mb-1">Reason (optional):</label>
                    <input
                      type="text"
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      className="retro-input w-full text-xs"
                      placeholder="Enter reason..."
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <button 
                    className="retro-button flex-1"
                    onClick={() => handleAction(selectedUser.user_id, selectedUser.action, actionReason || null)}
                  >
                    Confirm
                  </button>
                  <button 
                    className="retro-button flex-1"
                    onClick={() => {
                      setSelectedUser(null);
                      setActionReason("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="retro-window w-full max-w-lg max-h-[80vh] flex flex-col" data-testid="notes-modal">
              <div className="retro-title-bar">
                <span className="flex items-center gap-2">
                  <StickyNote className="w-4 h-4" />
                  Notes for {showNotesModal.username}
                </span>
                <button 
                  className="close-button-visible"
                  onClick={() => setShowNotesModal(null)}
                >
                  ✕
                </button>
              </div>
              <div className="p-4 bg-[#C0C0C0] flex-1 overflow-hidden flex flex-col">
                {/* Add Note Form */}
                <div className="mb-3">
                  <label className="font-arial text-xs font-bold block mb-1">Add New Note:</label>
                  <div className="flex gap-2">
                    <textarea
                      className="retro-input flex-1 text-xs resize-none h-16"
                      placeholder="Enter your note about this user..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      data-testid="note-input"
                    />
                    <button
                      className="retro-button px-4 bg-[#000080] text-white border-[#000080]"
                      onClick={handleAddNote}
                      disabled={!noteText.trim()}
                    >
                      Add
                    </button>
                  </div>
                </div>
                
                {/* Notes List */}
                <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2">
                  <h4 className="font-arial text-xs font-bold mb-2 text-[#7B0099]">
                    Note History ({userNotes.length})
                  </h4>
                  
                  {loadingNotes ? (
                    <p className="text-center text-xs text-[#808080]">Loading notes...</p>
                  ) : userNotes.length === 0 ? (
                    <p className="text-center text-xs text-[#808080] py-4">No notes for this user yet</p>
                  ) : (
                    <div className="space-y-2">
                      {userNotes.map((note, i) => (
                        <div key={i} className="retro-window p-2 text-xs">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-bold text-[#000080]">{note.added_by}</span>
                            <span className="text-[#808080] text-[10px]">{formatDate(note.created_at)}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Permissions Modal - Super Admin Only */}
        {showPermissionsModal && isSuperAdmin && editingPermissions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="retro-window w-full max-w-5xl max-h-[90vh] flex flex-col" data-testid="permissions-modal">
              <div className="retro-title-bar">
                <span className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Role Permissions Management
                  <Lock className="w-3 h-3 text-yellow-300" />
                </span>
                <button 
                  className="close-button-visible"
                  onClick={() => setShowPermissionsModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="p-4 bg-[#C0C0C0] flex-1 overflow-hidden flex flex-col">
                <div className="retro-inset p-2 mb-3 bg-yellow-50">
                  <p className="font-arial text-xs text-center">
                    <Crown className="w-4 h-4 inline mr-1 text-yellow-500" />
                    <strong>Super Admin Only:</strong> Toggle on/off what Admins and Moderators can do. Changes apply immediately after saving.
                  </p>
                </div>
                
                {/* Quick Actions */}
                <div className="flex gap-2 mb-3 flex-wrap">
                  <button
                    className="retro-button text-xs px-2 py-1 bg-purple-100"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      if (!newPerms.executive_admin) newPerms.executive_admin = {};
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        newPerms.executive_admin[key] = true;
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✓ Enable All Exec
                  </button>
                  <button
                    className="retro-button text-xs px-2 py-1 bg-purple-50"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      if (!newPerms.executive_admin) newPerms.executive_admin = {};
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        newPerms.executive_admin[key] = false;
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✗ Disable All Exec
                  </button>
                  <button
                    className="retro-button text-xs px-2 py-1 bg-red-100"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        newPerms.admin[key] = true;
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✓ Enable All Admin
                  </button>
                  <button
                    className="retro-button text-xs px-2 py-1 bg-red-50"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        if (key !== 'make_admin' && key !== 'remove_admin') {
                          newPerms.admin[key] = false;
                        }
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✗ Disable All Admin
                  </button>
                  <button
                    className="retro-button text-xs px-2 py-1 bg-blue-100"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        newPerms.moderator[key] = true;
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✓ Enable All Mod
                  </button>
                  <button
                    className="retro-button text-xs px-2 py-1 bg-blue-50"
                    onClick={() => {
                      const newPerms = { ...editingPermissions };
                      Object.keys(PERMISSION_LABELS).forEach(key => {
                        newPerms.moderator[key] = false;
                      });
                      setEditingPermissions(newPerms);
                    }}
                  >
                    ✗ Disable All Mod
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto retro-scrollbar">
                  <div className="grid grid-cols-4 gap-3">
                    {/* Executive Admin Permissions */}
                    <div className="retro-window p-3 bg-purple-50">
                      <h3 className="font-verdana text-sm font-bold text-purple-600 mb-3 flex items-center gap-2 border-b pb-2">
                        <Crown className="w-4 h-4" /> Executive Admin
                      </h3>
                      <div className="space-y-1.5">
                        {Object.keys(PERMISSION_LABELS).map(key => (
                          <div 
                            key={key} 
                            className={`flex items-center justify-between p-1.5 rounded cursor-pointer transition-colors ${
                              editingPermissions.executive_admin?.[key] ? 'bg-green-100 hover:bg-green-200' : 'bg-red-50 hover:bg-red-100'
                            }`}
                            onClick={() => togglePermission('executive_admin', key)}
                          >
                            <span className="font-arial text-xs">{PERMISSION_LABELS[key]}</span>
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              editingPermissions.executive_admin?.[key] ? 'bg-green-500' : 'bg-gray-400'
                            }`}>
                              <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                                editingPermissions.executive_admin?.[key] ? 'right-0.5' : 'left-0.5'
                              }`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Admin Permissions */}
                    <div className="retro-window p-3">
                      <h3 className="font-verdana text-sm font-bold text-red-600 mb-3 flex items-center gap-2 border-b pb-2">
                        <Shield className="w-4 h-4" /> Admin Permissions
                      </h3>
                      <div className="space-y-1.5">
                        {Object.keys(PERMISSION_LABELS).map(key => (
                          <div 
                            key={key} 
                            className={`flex items-center justify-between p-1.5 rounded cursor-pointer transition-colors ${
                              editingPermissions.admin[key] ? 'bg-green-100 hover:bg-green-200' : 'bg-red-50 hover:bg-red-100'
                            }`}
                            onClick={() => togglePermission('admin', key)}
                          >
                            <span className="font-arial text-xs">{PERMISSION_LABELS[key]}</span>
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              editingPermissions.admin[key] ? 'bg-green-500' : 'bg-gray-400'
                            }`}>
                              <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                                editingPermissions.admin[key] ? 'right-0.5' : 'left-0.5'
                              }`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Moderator Permissions */}
                    <div className="retro-window p-3">
                      <h3 className="font-verdana text-sm font-bold text-blue-600 mb-3 flex items-center gap-2 border-b pb-2">
                        <Award className="w-4 h-4" /> Moderator Permissions
                      </h3>
                      <div className="space-y-1.5">
                        {Object.keys(PERMISSION_LABELS).map(key => (
                          <div 
                            key={key} 
                            className={`flex items-center justify-between p-1.5 rounded cursor-pointer transition-colors ${
                              editingPermissions.moderator[key] ? 'bg-green-100 hover:bg-green-200' : 'bg-red-50 hover:bg-red-100'
                            }`}
                            onClick={() => togglePermission('moderator', key)}
                          >
                            <span className="font-arial text-xs">{PERMISSION_LABELS[key]}</span>
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              editingPermissions.moderator[key] ? 'bg-green-500' : 'bg-gray-400'
                            }`}>
                              <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                                editingPermissions.moderator[key] ? 'right-0.5' : 'left-0.5'
                              }`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* VIP Permissions */}
                    <div className="retro-window p-3 bg-yellow-50">
                      <h3 className="font-verdana text-sm font-bold text-yellow-600 mb-3 flex items-center gap-2 border-b pb-2">
                        <Star className="w-4 h-4" /> VIP Permissions
                      </h3>
                      <p className="text-[10px] text-gray-500 mb-3">Default permissions for all VIP users</p>
                      <div className="space-y-3">
                        <div 
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            editingPermissions.vip?.bypass_room_capacity !== false ? 'bg-green-100' : 'bg-red-50'
                          }`}
                          onClick={() => {
                            setEditingPermissions(prev => ({
                              ...prev,
                              vip: {
                                ...prev.vip,
                                bypass_room_capacity: prev.vip?.bypass_room_capacity === false ? true : false
                              }
                            }));
                          }}
                        >
                          <div>
                            <span className="font-arial text-xs font-bold">Access Full Rooms</span>
                            <p className="text-[10px] text-gray-500">Can join rooms even when at capacity</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full relative transition-colors ${
                            editingPermissions.vip?.bypass_room_capacity !== false ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                              editingPermissions.vip?.bypass_room_capacity !== false ? 'right-0.5' : 'left-0.5'
                            }`} />
                          </div>
                        </div>
                        <div 
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            editingPermissions.vip?.no_kick_ban !== false ? 'bg-green-100' : 'bg-red-50'
                          }`}
                          onClick={() => {
                            setEditingPermissions(prev => ({
                              ...prev,
                              vip: {
                                ...prev.vip,
                                no_kick_ban: prev.vip?.no_kick_ban === false ? true : false
                              }
                            }));
                          }}
                        >
                          <div>
                            <span className="font-arial text-xs font-bold">No Kick/Ban</span>
                            <p className="text-[10px] text-gray-500">Cannot be kicked or banned except by Super Admin</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full relative transition-colors ${
                            editingPermissions.vip?.no_kick_ban !== false ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                              editingPermissions.vip?.no_kick_ban !== false ? 'right-0.5' : 'left-0.5'
                            }`} />
                          </div>
                        </div>
                        <div 
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            editingPermissions.vip?.vip_badge !== false ? 'bg-green-100' : 'bg-red-50'
                          }`}
                          onClick={() => {
                            setEditingPermissions(prev => ({
                              ...prev,
                              vip: {
                                ...prev.vip,
                                vip_badge: prev.vip?.vip_badge === false ? true : false
                              }
                            }));
                          }}
                        >
                          <div>
                            <span className="font-arial text-xs font-bold">VIP Badge</span>
                            <p className="text-[10px] text-gray-500">Show crown icon next to username</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full relative transition-colors ${
                            editingPermissions.vip?.vip_badge !== false ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all shadow ${
                              editingPermissions.vip?.vip_badge !== false ? 'right-0.5' : 'left-0.5'
                            }`} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <button
                    className="retro-button flex-1 py-2 bg-[#008000] text-white border-[#008000] font-bold"
                    onClick={handleSavePermissions}
                    data-testid="save-permissions-btn"
                  >
                    💾 Save Permissions
                  </button>
                  <button
                    className="retro-button flex-1 py-2"
                    onClick={() => setShowPermissionsModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIP Permissions Modal */}
        {selectedUserForVIP && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" data-testid="vip-permissions-modal">
            <div className="retro-window w-[400px]">
              <div className="retro-title-bar flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  VIP Permissions: {selectedUserForVIP.username}
                </span>
                <button onClick={() => setSelectedUserForVIP(null)} className="text-white hover:bg-red-600 px-2">✕</button>
              </div>
              <div className="p-4">
                <p className="text-xs text-[#808080] mb-4">Configure special permissions for this VIP member:</p>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vipPermissionsForm.bypass_room_capacity}
                      onChange={(e) => setVipPermissionsForm(prev => ({...prev, bypass_room_capacity: e.target.checked}))}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="text-sm font-bold">Access Full Rooms</p>
                      <p className="text-xs text-[#808080]">Can join rooms even when at capacity</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vipPermissionsForm.no_kick_ban}
                      onChange={(e) => setVipPermissionsForm(prev => ({...prev, no_kick_ban: e.target.checked}))}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="text-sm font-bold">No Kick/Ban Protection</p>
                      <p className="text-xs text-[#808080]">Cannot be kicked or banned (except by Super Admin)</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={vipPermissionsForm.vip_badge}
                      onChange={(e) => setVipPermissionsForm(prev => ({...prev, vip_badge: e.target.checked}))}
                      className="w-4 h-4"
                    />
                    <div>
                      <p className="text-sm font-bold">VIP Badge</p>
                      <p className="text-xs text-[#808080]">Show crown icon next to username</p>
                    </div>
                  </label>
                </div>
                
                <div className="flex gap-2 mt-6">
                  <button
                    className="retro-button flex-1 py-2 bg-[#C0C0C0]"
                    onClick={() => setSelectedUserForVIP(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="retro-button flex-1 py-2 bg-yellow-400"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/action`, {
                          user_id: selectedUserForVIP.user_id,
                          action: "update_vip_permissions",
                          permissions: vipPermissionsForm
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`VIP permissions updated for ${selectedUserForVIP.username}`);
                        setSelectedUserForVIP(null);
                        fetchData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to update permissions");
                      }
                    }}
                  >
                    Save Permissions
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DraggableResizableWindow>

      {/* Moderation Action Modal */}
      {showModerationModal && moderationTargetUser && (
        <ModerationActionModal 
          targetUser={moderationTargetUser}
          onClose={() => { setShowModerationModal(false); setModerationTargetUser(null); }}
          onActionComplete={() => { fetchData(); }}
        />
      )}
    </div>
  );
};

export default AdminPanel;
